import { useEffect, useRef, useMemo, useState, useContext, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Create } from '../features/Create.jsx';
import { useFileDrop } from '../hooks/useFileDrop.js';
import { useIndexStatusPolling } from '../hooks/useIndexStatusPolling.js';
import { useNodeListing } from '../hooks/useNodeListing.js';
import { MAX_SELECTION, useNodeSelection } from '../hooks/useNodeSelection.js';
import { CanvasMenuItem, CanvasMenuSeparator } from './listing/CanvasMenu.jsx';
import { FacetBar } from './listing/FacetBar.jsx';
import { ActionRequiredBanner } from './listing/ActionRequiredBanner.jsx';
import { IndexStatusBadge } from './listing/IndexStatusBadge.jsx';
import { KbdChip } from '@/shared/components/KbdChip.jsx';
import { openCommandPalette } from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { BulkActionBar } from './listing/BulkActionBar.jsx';
import { DragOverlay } from './listing/DragOverlay.jsx';
import { FolderPathBar } from './listing/FolderPathBar.jsx';
import { ListingToolbar } from './listing/ListingToolbar.jsx';
import { LocationTabs } from './listing/LocationTabs.jsx';
import { TabPill } from './listing/TabPill.jsx';
import { VisibilityBadge } from './listing/VisibilityBadge.jsx';
import { useLoad, useGetRequest, usePostRequest, usePatchRequest, useDeleteRequest } from '@/shared/hooks/requests.js';
import { NODE, NODE_COUNTS, NODE_RESTORE_TRASH, NODE_PURGE, NODE_BULK_DELETE, FILE, FILE_CLONE, FILE_GENERATE_BLANK, FILE_ACTION_COUNT, RECEIVED_SHARES, SENT_SHARES, FILE_ARCHIVE, FILE_RESTORE, FILE_RESTORE_TARGET, TEMPLATE_INSTANTIATE } from '@/shared/utils/urls.js';
import { SharedDocument, SharedFolder } from '@/modules/shared/components/SharedTable.jsx';
import { tagShareItems } from '@/modules/shared/utils.js';
import { DeleteShareModal } from '@/modules/shared/components/DeleteShare.jsx';

import { FileTypeIcon } from '@/shared/ui/FileTypeIcon.jsx';
import { Context } from '@/shared/contexts/GlobalContext.jsx';
import { Link } from 'react-router-dom';
import {
  Bot,
  FileText,
  FolderIcon as FolderLucide,
  Library,
  BookMarked,
  Plus,
  FilePlus,
  Search as SearchIcon,
  List,
  LayoutGrid,
  Lock,
  Globe,
  Upload as UploadIcon,
  Loader2,
  Check,
  CheckCircle,
  X,
  Share2,
  Eye,
  Pencil,
  PenLine,
  Copy,
  History,
  FileType,
  Activity,
  MessageSquare,
  Trash2,
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  File,
  FileSpreadsheet,
  Presentation,
  Folder,
  MoreHorizontal,
  Home,
  PenTool,
  UserCheck,
  AlertCircle,
  CircleDashed,
  Users,
  Archive,
  ArchiveRestore,
  Info,
  UserCog,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  LockKeyhole,
  LockKeyholeOpen,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArchiveConfirmDialog, RestoreConfirmDialog, BulkDeleteDialog, PermanentDeleteDialog } from '@/shared/ui/ArchiveRestoreDialogs.jsx';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { canFileNode, canRegister, showsRecords, useRecordsAccess } from '@/modules/records/lib/api.js';
import { RegisterDialog } from '@/modules/records/components/RegisterDialog.jsx';
import { FileToCaseDialog } from '@/modules/records/components/FileToCaseDialog.jsx';
import { RecordBadge } from '@/modules/records/components/RecordBadge.jsx';
import { LockBadge } from './listing/LockBadge.jsx';
import { LockFileDialog } from '../features/LockFileDialog.jsx';

import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';

import { DeleteFolder } from '../features/Delete.jsx';
import { DeleteFile } from '../features/DeleteFile.jsx';
import { SharingModal } from '@/modules/shared/components/SharingModal.jsx';
import { ChangeVisibilityModal } from '../features/ChangeVisibilityModal.jsx';
import { VersionHistoryModal } from '@/modules/files/features/VersionHistoryModal.jsx';
import { DocumentTypeSelector } from '@/modules/signing/components/DocumentTypeSelector.jsx';
import { TemplatePicker } from '@/modules/templates/components/TemplatePicker.jsx';
import { FillFromRecordDialog } from '@/modules/templates/components/FillFromRecordDialog.jsx';
import { TemplateBadge } from './listing/TemplateBadge.jsx';
import { ActivityModal } from '../features/ActivityModal.jsx';
import { CommentsDrawer } from '../features/CommentsDrawer.jsx';
import { DocumentEditorModal } from '@/shared/ui/DocumentEditorModal.jsx';
import { UpdateFolder, RenameFile } from '../features/Update.jsx';
import { FileCreate } from '@/modules/my-docs/features/FileCreate.jsx';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { isCollaboraEditable, fileExtension } from '@/shared/utils/fileKind.js';
import { MoveFileModal } from '../features/MoveFileModal.jsx';
import { VisibilityControl } from '@/shared/ui/VisibilityControl.jsx';
import { PropertiesModal } from '@/shared/ui/PropertiesModal.jsx';

// Get card background based on file type
// Cards sit flat on the page — no tinted tile behind the icon. Files and
// folders must read identically, and folders never had one; the file-type
// signal already comes from the branded icon itself. (Used by both grid
// tiles and the small list-view boxes, which layer drag-over/selection
// states on top via cn().)
/** Card surface. Deliberately transparent in dark mode — the grid shows through. */
const CARD_SURFACE = 'bg-white dark:bg-transparent';
/** The big 135px tiles: an elevated card that lifts + glows on hover (the aip
 *  language). bg-card is white on light / a raised surface on dark. */
const CARD_GLASS =
  'bg-card border border-border/60 shadow-e1 transition-all duration-300 ' +
  'group-hover:-translate-y-1 group-hover:shadow-[0_16px_38px_-12px_var(--glow)]';

/** Shimmer placeholder grid — replaces the black initial-load spinner. */
function CardsSkeleton() {
  return (
    <div className="fade-slide-in grid grid-cols-[repeat(auto-fill,135px)] gap-x-6 gap-y-8 justify-between max-md:justify-evenly content-start">
      {Array.from({ length: 14 }).map((_, i) => (
        <div key={i} className="flex h-[135px] w-[135px] flex-col justify-between rounded-xl border border-border/60 bg-card p-4 shadow-e1">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-1.5">
            <Skeleton className="h-3 w-4/5" />
            <Skeleton className="h-2.5 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}


import { DocumentModal } from '@/shared/ui/DocumentViewerModal';
import { atLeast, can, canEditFile, canHandOver, canLockFile, canUnlockFile, editBlock, isOwner } from '@/shared/utils/nodeAccess.js';
import { iHold } from '@/shared/auth/me.js';
import { SCOPE } from '@/shared/auth/scopes.js';
import { AllowAssistantDialog } from '@/modules/assistant/components/AllowAssistantDialog.jsx';
import { mayAllow, useGrantsAvailable } from '@/modules/assistant/libraryGrants.js';
import { TransferOwnerModal } from '@/modules/shared/components/TransferOwnerModal.jsx';

// A docs uuid (what `?file=` and `/files/:id` carry).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The folder a row sits in (its uuid), or null at the root. */
const parentOf = (node) => node?.parentUuid ?? node?.parent_uuid ?? null;

export const FoldersTable = () => {
  const [open, setOpen] = useState(false);
  // Infinite scroll: files accumulate across pages instead of replacing.
  const [accFiles, setAccFiles] = useState([]);
  const [filesHasMore, setFilesHasMore] = useState(false);
  const accKeyRef = useRef(''); // query-context key for replace-vs-append decisions
  // List-view column sort (backend-driven). Default newest-first.
  const dropdownRef = useRef(null);

  const sortArrow = (col) => {
    if (sortBy !== col) return <ChevronsUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };
  // Scroll lives directly on the list/grid containers (fixed viewport-based
  // height) rather than a flex-bounded parent — simpler and iframe-proof.
  // Defined below (after actionCount) since the banner changes the offset.

  // Global drag-and-drop overlay state
  const [draggedFiles, setDraggedFiles] = useState(null);

  // `/files/:id`: the open folder's uuid (none at the root).
  const { id: folderUuid } = useParams();
  // `?file=<uuid>` (the deep link `/my-docs?folder=…&file=…` lands here)
  // opens that file's viewer once, then leaves the URL — a later navigation
  // must not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepFile = searchParams.get('file');
  const { t } = useTranslation(['common', 'my-docs', 'mainPage', 'shared', 'search', 'records', 'assistant']);
  const navigate = useNavigate();
  const recordsAccess = useRecordsAccess();
  const [registering, setRegistering] = useState(null);
  const [filing, setFiling] = useState(null); // { node, mode: 'file' | 'folder' | 'link' }
  // Touch devices have no double-click: a single tap must OPEN (folders
  // navigate, files preview) instead of only selecting.
  const isMobileView = useIsMobile();

  // Navigation history management
  const [navigationHistory, setNavigationHistory] = useState([
    { uuid: null, name: t('my-docs:title') }
  ]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);

  /**
   * Jump to a point in the trail. Back, forward and clicking a crumb are the
   * same operation on different indices — they were three copies of this.
   * The root entry has no uuid, so it routes to the listing rather than a folder.
   */
  const goToHistoryIndex = useCallback(
    (index) => {
      const target = navigationHistory[index];
      if (!target) return;
      setCurrentHistoryIndex(index);
      navigate(target.uuid ? `/files/${target.uuid}` : '/my-docs');
    },
    [navigationHistory, navigate],
  );

  const [previewDoc, setPreviewDoc] = useState(null);
  const { viewMode, setViewMode } = useContext(Context);

  // What the listing shows — location, facets, sort, search, pagination —
  // and the request they produce. See `useNodeListing`.
  const listing = useNodeListing();
  const {
    location: activeFilter,
    setLocation: setActiveFilter,
    typeFilter, setTypeFilter,
    visFilter, setVisFilter,
    actionOnly, setActionOnly,
    sortBy, sortDir, toggleSort,
    searchValue, setSearchValue, clearSearch, debouncedSearch,
    setPage: setFilesPage,
    params: loadFilesParams,
    result: loadFiles,
  } = listing;

  const warnSelectionCap = () =>
    toast.info(
      t('my-docs:bulk.selectionCap', { max: MAX_SELECTION })
        || `You can select up to ${MAX_SELECTION} items at a time`,
      { id: 'selection-cap' }, // fixed id → repeated hits replace, never stack
    );

  // File-explorer selection: click / ctrl-click / shift-range, capped, and
  // reset whenever the dataset underneath it changes.
  // Declared before the selection hook: selecting with the mouse returns
  // focus here, so the hook needs the ref at call time.
  const listContainerRef = useRef(null);

  const {
    selectedUuids, setSelectedUuids,
    anchorUuid, setAnchorUuid, focusUuid, setFocusUuid,
    selectionCount, selectRow, toggleSelect, toggleSelectAll,
    applyRange, clearSelection, orderedIndex,
  } = useNodeSelection({
    items: accFiles,
    resetKey: `${activeFilter}:${folderUuid ?? 'root'}`,
    onCapped: warnSelectionCap,
    containerRef: listContainerRef,
  });

  // Live index-status: poll ONLY the files still being indexed and patch rows
  // in place — refetching would collapse the scroll-accumulated buffer.
  // Identity-preserving: untouched rows keep their object so the virtualizer
  // and memoized children skip them.
  const patchIndexStatuses = useCallback((statuses) => {
    setAccFiles((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const status = statuses[row.uuid];
        if (!status || status === (row.indexStatus || row.index_status)) return row;
        changed = true;
        return { ...row, indexStatus: status, index_status: status };
      });
      return changed ? next : prev;
    });
  }, []);

  // My Documents secondary row = three INDEPENDENT facets that AND together.
  // Type  → backend `approval_state` ('ordinary'|'agreement'|'signed'); 'all' = off.
  // Vis   → backend `visibility` ('private'|'public'); 'any' = off.
  // Status→ backend `action_required` boolean toggle.


  // Shared-with-me overlay: when on, the filter row swaps to Sent/Received
  // tabs and the grid below shows shared files instead of the user's docs.
  // Pressing the back arrow restores the my-docs filter and grid as-is.
  const [sharedView, setSharedView] = useState(false);
  const [sharedMode, setSharedMode] = useState('received');
  const getReceivedShared = useGetRequest({ url: RECEIVED_SHARES });
  const getSentShared = useGetRequest({ url: SENT_SHARES });

  // One mixed, type-tagged list from the unified share endpoints.
  const sharedShares =
    sharedMode === 'received' ? (getReceivedShared.response || []) : (getSentShared.response || []);
  const sharedItems = tagShareItems(sharedShares);
  const sharedLoading = sharedMode === 'received' ? getReceivedShared.loading : getSentShared.loading;

  useEffect(() => {
    if (!sharedView) return;
    if (sharedMode === 'received') getReceivedShared.request();
    else getSentShared.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedView, sharedMode]);

  // Folder navigation must land on the folder's contents. Root → folder
  // already does (route change remounts the component), but folder → folder
  // only swaps the :id param, so without this the overlay would keep
  // showing the stale global share list over the new URL.
  useEffect(() => { setSharedView(false); }, [folderUuid]);

  // File operation modals
  const [deleteFileOpen, setDeleteFileOpen] = useState(false);
  const [shareFileOpen, setShareFileOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [docTypeOpen, setDocTypeOpen] = useState(false);
  const [templatesModalOpen, setTemplatesModalOpen] = useState(false);
  // {template, language}: a template about another module's record, waiting
  // for that record to be chosen (FillFromRecordDialog).
  const [fillingTemplate, setFillingTemplate] = useState(null);
  // Folder-create dialog, now driven from the "+ New" menu (Create runs controlled).
  const [folderCreateOpen, setFolderCreateOpen] = useState(false);
  // Right-click-empty-canvas create menu: null, or {x, y} screen coords.
  const [createMenu, setCreateMenu] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [propsResource, setPropsResource] = useState(null);
  const [propsType, setPropsType] = useState('file');
  const openProps = (item, type) => { setPropsResource(item); setPropsType(type); setPropsOpen(true); };
  const [sharedDeleteOpen, setSharedDeleteOpen] = useState(false);
  const [selectedShareItem, setSelectedShareItem] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [moveFileOpen, setMoveFileOpen] = useState(false);

  // Multi-select for bulk move/copy/delete. `moveMode` decides which the
  // shared MoveFileModal performs when opened from the floating toolbar.
  // Files and folders share the nodes' uuids, so ONE Set covers both;
  // items carry `type` when a per-type split is needed.
  // File-explorer selection model: `anchorUuid` is the pivot a Shift-range
  // extends from; `focusUuid` is the active row the keyboard moves. The scroll
  // container is focused so it can receive arrow/Enter keystrokes.
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Last-known My Documents total — shown as the count on the My Documents
  // location tab (replaces the old "N items" line). Captured while viewing My
  // Documents so it stays put when you switch to Shared/Archive/Trash.
  const [myDocsCount, setMyDocsCount] = useState(null);
  const [moveMode, setMoveMode] = useState('move');
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkFolderUuids, setBulkFolderUuids] = useState([]);
  // Lock… / Unlock from a file's menu: {file, mode: 'lock' | 'unlock'}.
  const [locking, setLocking] = useState(null);

  // Archive / restore. Signed E-IMZO docs are archive-only (cannot delete).
  // The hooks are URL-less; each call passes the selected file's URL so we
  // don't depend on `selectedFile` being committed before the request fires.
  const archiveFile = usePostRequest({});
  const restoreFile = usePostRequest({});
  const restoreTargetReq = useGetRequest({});
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreNeedsPick, setRestoreNeedsPick] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const handleConfirmArchive = async () => {
    if (!selectedFile) return;
    const { success } = await archiveFile.request({
      url: FILE_ARCHIVE.replace('{file_uuid}', selectedFile.uuid),
      data: {},
    });
    if (success) {
      toast.success(t('my-docs:archive.success'));
      setArchiveOpen(false);
      refreshData();
    } else {
      toast.error(t('my-docs:archive.error'));
    }
  };

  const handleOpenRestore = async (item) => {
    setSelectedFile(item);
    try {
      const { response } = await restoreTargetReq.request({
        url: FILE_RESTORE_TARGET.replace('{file_uuid}', item.uuid),
      });
      setRestoreNeedsPick(Boolean(response?.needsFolderPick));
      setRestoreOpen(true);
    } catch (e) {
      console.error(e);
      toast.error(t('my-docs:restore.error'));
    }
  };

  const handleConfirmRestore = async () => {
    if (!selectedFile) return;
    const { success } = await restoreFile.request({
      url: FILE_RESTORE.replace('{file_uuid}', selectedFile.uuid),
      // Its folder is gone: back to the root.
      data: restoreNeedsPick ? { newFolderUuid: null } : {},
    });
    if (success) {
      toast.success(t('my-docs:restore.success'));
      setRestoreOpen(false);
      refreshData();
    } else {
      toast.error(t('my-docs:restore.error'));
    }
  };

  const handleRequestEdit = useCallback((item) => {
    // Collabora only edits office formats + txt; anything else (yml, json,
    // md…) it would open as a read-only viewer. Refuse with an explicit
    // message instead of routing to an editor that can't edit.
    if (!isCollaboraEditable(item)) {
      const ext = fileExtension(item) || 'this';
      toast.error(
        t('my-docs:fileErrors.editUnsupportedType', { ext })
        || `Editing .${ext} files is not supported`,
      );
      return;
    }
    // Who may edit what is the backend's one rule (`edit_block` / `can_edit`
    // on the read); the menus only offer Edit where it holds, and the editor
    // re-checks it while open.
    setSelectedFile(item);
    setEditorOpen(true);
  }, [t]);

  // Streaming generation state
  
  // Drag-and-drop target state
  const [draggedOverFolderUuid, setDraggedOverFolderUuid] = useState(undefined);

  // Load folder info if in folder (unified node endpoint)
  const loadFolderInfo = useGetRequest({
    url: folderUuid ? NODE.replace('{node_uuid}', folderUuid) : null
  });

  // The deep-linked file is fetched by uuid — the listing is paged and the
  // file may not be on the first page — and handed to the viewer.
  const loadDeepFile = useGetRequest({ showError: false });
  useEffect(() => {
    if (!deepFile || !UUID_RE.test(deepFile)) return;
    let cancelled = false;
    loadDeepFile.request({ url: NODE.replace('{node_uuid}', deepFile) }).then(({ response, success }) => {
      if (cancelled) return;
      if (success && response && response.type !== 'folder') setPreviewDoc(response);
      const next = new URLSearchParams(searchParams);
      next.delete('file');
      setSearchParams(next, { replace: true });
    });
    return () => { cancelled = true; };
    // once per deep link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepFile]);

  // Update navigation history when entering a folder
  useEffect(() => {
    if (folderUuid && loadFolderInfo.response && loadFolderInfo.response.uuid === folderUuid) {
      const folderName = loadFolderInfo.response.name;

      // Check if this folder is already in history
      const existingIndex = navigationHistory.findIndex(item => item.uuid === folderUuid);

      if (existingIndex === -1) {
        // New folder - add to history
        const newHistory = [
          ...navigationHistory.slice(0, currentHistoryIndex + 1),
          { uuid: folderUuid, name: folderName }
        ];
        setNavigationHistory(newHistory);
        setCurrentHistoryIndex(newHistory.length - 1);
      } else {
        // Heal a possibly stale name (e.g. from an earlier race where
        // the entry was added before its own response arrived).
        if (navigationHistory[existingIndex].name !== folderName) {
          const updated = [...navigationHistory];
          updated[existingIndex] = { ...updated[existingIndex], name: folderName };
          setNavigationHistory(updated);
        }
        if (currentHistoryIndex !== existingIndex) {
          setCurrentHistoryIndex(existingIndex);
        }
      }
    } else if (!folderUuid && currentHistoryIndex !== 0) {
      // At root - reset only if not already at 0
      setCurrentHistoryIndex(0);
    }
  }, [folderUuid, loadFolderInfo.response, navigationHistory, currentHistoryIndex]);

  useEffect(() => {
    if (folderUuid) {
      loadFolderInfo.request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderUuid]);

  const currentFolder = loadFolderInfo.response;

  // Pending-action count drives the Action Required tab badge and the amber
  // banner above the tab strip. Folder-scoped like every other tab filter
  // (root = files outside folders), and refetched on navigation so the badge
  // always matches what the Action Required tab would list here.
  const loadActionCount = useLoad(
    { url: FILE_ACTION_COUNT, params: folderUuid ? { parent_uuid: folderUuid } : {} },
    [folderUuid],
  );
  const actionCount = loadActionCount.response?.count || 0;

  // Whether the amber "documents waiting" banner renders above the tab strip.
  // Hidden once the Status facet is already filtering to those files.
  const actionBannerVisible = actionCount > 0 && !actionOnly;

  // Counts for the primary location tabs (shared / archived / trash). Refetched
  // by refreshData after any mutation so the badges stay honest.
  const loadCounts = useLoad({ url: NODE_COUNTS }, []);
  const counts = loadCounts.response || {};

  // Primary navigation = LOCATION (a dataset), derived from the existing
  // sharedView flag + the archive/trash filter values. The secondary filter
  // row only applies inside My Documents.
  const navLocation = sharedView
    ? 'shared'
    : activeFilter === 'archived'
      ? 'archive'
      : activeFilter === 'trash'
        ? 'trash'
        : 'my_documents';
  useIndexStatusPolling({
    items: accFiles,
    enabled: navLocation === 'my_documents',
    onStatuses: patchIndexStatuses,
  });

  const goMyDocuments = () => {
    setSharedView(false);
    if (activeFilter === 'archived' || activeFilter === 'trash') setActiveFilter('all');
  };
  const goLocation = (filterValue) => { setSharedView(false); setActiveFilter(filterValue); };
  useEffect(() => {
    if (navLocation === 'my_documents' && loadFiles.response && !Array.isArray(loadFiles.response)
        && typeof loadFiles.response.total === 'number') {
      setMyDocsCount(loadFiles.response.total);
    }
  }, [loadFiles.response, navLocation]);

  // The page root is `h-screen flex flex-col`, so the content area's flex-1
  // already ends exactly at the viewport bottom — the scroll containers just
  // fill it. (Replaces hardcoded `max-h-[calc(100vh-…)]` viewport math that
  // drifted whenever the header stack or banner changed height.)
  const scrollBoxClass = 'flex-1 min-h-0 overflow-auto';

  const refreshData = useCallback(() => {
    // Mutations (move/delete/visibility/copy) reset the list to page 1. We do
    // NOT clear accFiles first: emptying it unmounts every rendered row, and the
    // heavy per-row Radix subtrees leak detached DOM that retains a full accFiles
    // snapshot — repeated on every mutation, that was the multi-GB leak. The
    // page-1 response replaces the buffer in place (accumulator: respPage===1 →
    // returns fresh `files`), so React diffs by key and reuses the live rows.
    setFilesPage(1);
    // The hook owns which endpoint each location reads from; reuse its
    // resolved url/params instead of rebuilding the mapping here.
    loadFiles.request({ url: listing.url, params: { ...listing.params, page: 1 } });
    // The archive tab doesn't display the action-count badge, so skip the
    // refetch there — pure waste otherwise.
    if (activeFilter !== 'archived') {
      loadActionCount.request();
    }
    // Location-tab badges shift after any mutation (delete → trash++, etc.).
    loadCounts.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, loadFilesParams]);

  const cloneFile = usePostRequest({});
  const moveFile = usePatchRequest({});
  const bulkDelete = usePostRequest({ url: NODE_BULK_DELETE });
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Type splits derive from the visible items (selection always originates
  // from rendered rows, which live in the accumulated buffer, accFiles).
  const selectedFolderCount = accFiles.filter(
    (i) => i.type === 'folder' && selectedUuids.has(i.uuid)
  ).length;
  const hasFolderSelection = selectedFolderCount > 0;
  // The selected rows themselves ("Allow the assistant to read this" on a set).
  const selectedNodes = accFiles.filter((i) => selectedUuids.has(i.uuid));
  // Signed E-IMZO files in the selection can't be deleted — only archived.
  // Surfaced in the bulk menu/toolbar and the delete confirm dialog.
  const selectedSignedFiles = accFiles.filter(
    (i) => i.type === 'file'
      && selectedUuids.has(i.uuid)
      && (i.approvalState || i.approval_state) === 'signed'
  );
  const selectedSignedCount = selectedSignedFiles.length;
  const bulkDeletableCount = selectionCount - selectedSignedCount;

  // Trash restore — the only way out of the trash (nothing is ever hard-deleted).
  const restoreTrash = usePostRequest({});
  const restoreFromTrash = async (item) => {
    const { success } = await restoreTrash.request({
      url: NODE_RESTORE_TRASH.replace('{node_uuid}', item.uuid),
      data: {},
    });
    if (success) {
      toast.success(t('my-docs:trash.restored') || 'Restored');
      refreshData();
    } else {
      toast.error(t('my-docs:trash.restoreError') || 'Could not restore');
    }
  };
  const handleBulkRestore = async () => {
    let restored = 0;
    for (const uuid of [...selectedUuids]) {
      const { success } = await restoreTrash.request({
        url: NODE_RESTORE_TRASH.replace('{node_uuid}', uuid), data: {},
      });
      if (success) restored += 1;
    }
    if (restored > 0) toast.success(t('my-docs:trash.restoredCount', { count: restored }));
    clearSelection();
    refreshData();
  };

  // Permanent (hard) delete from the trash. Manual cleanup only — no auto-purge.
  // The trigger functions just stage the targets + open the scary confirm; the
  // dialog's confirm does the actual irreversible delete.
  const purgeNode = useDeleteRequest({});
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeUuids, setPurgeUuids] = useState([]);
  const askPurge = (uuids) => { setPurgeUuids(uuids); setPurgeOpen(true); };
  const confirmPurge = async () => {
    let purged = 0;
    for (const uuid of purgeUuids) {
      const { success } = await purgeNode.request({ url: NODE_PURGE.replace('{node_uuid}', uuid) });
      if (success) purged += 1;
    }
    if (purged > 0) toast.success(t('my-docs:trash.purgedCount', { count: purged }));
    setPurgeOpen(false);
    setPurgeUuids([]);
    clearSelection();
    refreshData();
  };

  // Selection / bulk-op ceiling. Move, copy and delete all act on the selection,
  // so capping the selection caps every bulk action in one place.
  // ── File-explorer selection/navigation ──────────────────────────────────

  // Open = double-click / Enter. Folders navigate in; files open the preview.
  const openItem = (item) => {
    if (!item) return;
    // In the trash a node is soft-deleted, so the backend 404s on opening it
    // (folder listing / file fetch both filter deleted_at IS NULL). Opening
    // makes no sense here anyway — you restore first. Hint instead of erroring.
    if (navLocation === 'trash') {
      toast.info(t('my-docs:trash.restoreToOpen') || 'Restore this item to open it');
      return;
    }
    if (item.type === 'folder') navigate(`/files/${item.uuid}`);
    else setPreviewDoc(item);
  };

  // Cards view is a responsive grid — count how many cells share the top row so
  // Up/Down can jump a whole row.
  const getGridColumns = () => {
    const container = listContainerRef.current;
    if (!container) return 1;
    const els = [...container.querySelectorAll('[data-row-id]')];
    if (els.length < 2) return 1;
    const top0 = els[0].offsetTop;
    let cols = 1;
    for (let i = 1; i < els.length; i += 1) {
      if (els[i].offsetTop === top0) cols += 1;
      else break;
    }
    return cols;
  };

  const handleListKeyDown = (e) => {
    if (!accFiles.length) return;
    const isGrid = viewMode === 'cards';
    const focused = focusUuid != null ? accFiles.find((i) => i.uuid === focusUuid) : null;

    if (e.key === 'Enter') {
      if (focused) { e.preventDefault(); openItem(focused); }
      return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      setSelectedUuids(new Set(accFiles.slice(0, MAX_SELECTION).map((i) => i.uuid)));
      if (accFiles.length > MAX_SELECTION) warnSelectionCap();
      return;
    }
    if (e.key === 'Escape') { clearSelection(); return; }

    let delta = 0;
    if (e.key === 'ArrowDown') delta = isGrid ? getGridColumns() : 1;
    else if (e.key === 'ArrowUp') delta = isGrid ? -getGridColumns() : -1;
    else if (isGrid && e.key === 'ArrowRight') delta = 1;
    else if (isGrid && e.key === 'ArrowLeft') delta = -1;
    else return;

    e.preventDefault();
    const curIdx = focusUuid != null ? orderedIndex(focusUuid) : -1;
    const nextIdx = curIdx < 0
      ? 0
      : Math.max(0, Math.min(accFiles.length - 1, curIdx + delta));
    const nextUuid = accFiles[nextIdx].uuid;
    if (e.shiftKey && anchorUuid != null) {
      applyRange(anchorUuid, nextUuid, false);
    } else {
      setSelectedUuids(new Set([nextUuid]));
      setAnchorUuid(nextUuid);
    }
    setFocusUuid(nextUuid);
    if (isGrid) {
      // Grid isn't virtualized yet — every card is in the DOM, so scroll by uuid.
      requestAnimationFrame(() => {
        listContainerRef.current
          ?.querySelector(`[data-row-id="${nextUuid}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      });
    } else {
      // Virtualized list: ask the virtualizer to bring the row into view (it may
      // not be mounted yet).
      listVirtualizer.scrollToIndex(nextIdx, { align: 'auto' });
    }
  };

  // Click on empty space inside the list clears the selection — but NOT when
  // the press lands on the scrollbar gutter (offsetX past the content width),
  // otherwise dragging the scrollbar would wipe the selection.
  const handleListBackgroundMouseDown = (e) => {
    if (e.target === e.currentTarget && e.nativeEvent.offsetX > e.currentTarget.clientWidth) return;
    if (!e.target.closest('[data-row-id]')) clearSelection();
  };

  // Windowing for the list view: only the rows in/near the viewport are
  // mounted, so 500+ files cost ~20 rows of DOM instead of all of them.
  // Heights are measured per-row (measureElement) so variable rows stay exact.
  const listVirtualizer = useVirtualizer({
    count: viewMode === 'list' ? accFiles.length : 0,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 56,
    overscan: 10,
    getItemKey: (index) => {
      const it = accFiles[index];
      return it ? `${it.type}-${it.uuid}` : index;
    },
  });

  const openBulkMove = (copy = false) => {
    if (selectionCount === 0) return;
    // Folder copy isn't supported (recursive), so the Copy action is files-only.
    if (copy && hasFolderSelection) return;
    setMoveMode(copy ? 'copy' : 'move');
    const selected = accFiles.filter((i) => selectedUuids.has(i.uuid));
    setBulkFiles(selected.filter((i) => i.type !== 'folder').map((i) => ({ uuid: i.uuid })));
    setBulkFolderUuids(copy ? [] : selected.filter((i) => i.type === 'folder').map((i) => i.uuid));
    setMoveFileOpen(true);
  };

  // Archive the directly-selected signed files (no bulk endpoint exists, so we
  // fan out the per-file archive call). Returns how many succeeded.
  const archiveSignedSelection = async () => {
    let archived = 0;
    for (const f of selectedSignedFiles) {
      const { success } = await archiveFile.request({
        url: FILE_ARCHIVE.replace('{file_uuid}', f.uuid),
        data: {},
      });
      if (success) archived += 1;
    }
    return archived;
  };

  // Confirmed from BulkDeleteDialog: archive the signed files (they can't be
  // deleted), then bulk-delete the rest (folders + unsigned files). Signed docs
  // *inside* deleted folders are auto-archived by the backend.
  const handleConfirmBulkDelete = async () => {
    const archived = await archiveSignedSelection();

    const signedUuids = new Set(selectedSignedFiles.map((f) => f.uuid));
    const nodeUuids = [...selectedUuids].filter((uuid) => !signedUuids.has(uuid));

    let deleted = 0;
    let archivedInside = 0;
    if (nodeUuids.length > 0) {
      const { success, response } = await bulkDelete.request({ data: { nodeUuids } });
      if (!success) {
        toast.error(t('my-docs:bulk.error') || 'Bulk delete failed');
        return;
      }
      deleted = response?.deleted?.length ?? 0;
      archivedInside = response?.archivedSigned?.length ?? 0;
    }

    if (deleted > 0) {
      toast.success(t('my-docs:bulk.deleted', { count: deleted }));
    }
    const totalArchived = archived + archivedInside;
    if (totalArchived > 0) {
      toast.info(t('my-docs:bulk.archivedSigned', { count: totalArchived }));
    }
    setBulkDeleteOpen(false);
    clearSelection();
    refreshData();
  };

  // Handler for duplicating files. The backend derives the copy's name
  // itself ("name (1).ext" auto-suffix), so no payload is needed.
  const handleDuplicateFile = async (file) => {
    const { success } = await cloneFile.request({
      url: FILE_CLONE.replace('{file_uuid}', file.uuid),
      data: {},
    });

    if (success) {
      toast.success(t('my-docs:duplicate.success'));
      refreshData();
    }
  };

  // Blank document creation
  const createBlankDoc = usePostRequest({ url: FILE_GENERATE_BLANK });
  const [isCreatingBlank, setIsCreatingBlank] = useState(false);

  const handleCreateBlankDoc = useCallback(async () => {
    setIsCreatingBlank(true);
    try {
      // No visibility: the server gives the install's default.
      const body = {};
      if (folderUuid) body.parentUuid = folderUuid;
      const { response, success } = await createBlankDoc.request({ data: body });
      if (success && response?.uuid) {
        const newDoc = {
          ...response,
          name: response.name || t('common:editor.untitled'),
          approvalState: response.approvalState || 'ordinary',
          visibility: response.visibility || 'private',
        };
        setSelectedFile(newDoc);
        setEditorOpen(true);
        refreshData();
      } else {
        toast.error(t('my-docs:create.blankError') || 'Failed to create document');
      }
    } catch {
      toast.error(t('my-docs:create.blankError') || 'Failed to create document');
    } finally {
      setIsCreatingBlank(false);
    }
  }, [folderUuid, createBlankDoc, refreshData, t]);

  // "From template" must never open the template itself — instantiate it
  // (a clone tagged as a document that carries the template's placeholder
  // definitions, so the editor's Placeholders tab opens in fill mode) into
  // the current folder and open the new document in the editor.
  // `extra`: values and the record they came from, for a template about
  // another module's record (filled on the server as it is made).
  const instantiateTemplate = useCallback(async (template, language, extra = {}) => {
    const { response, success } = await cloneFile.request({
      url: TEMPLATE_INSTANTIATE.replace('{template_uuid}', template.uuid),
      // `language` picks the family member (the picker resolves it from the
      // UI language; null lets the server fall back to the template itself).
      data: { parentUuid: folderUuid || null, language, ...extra },
    });
    if (success && response?.uuid) {
      setSelectedFile({
        ...response,
        name: response.name || t('my-docs:create.newDocument'),
      });
      setEditorOpen(true);
      refreshData();
      toast.success(t('my-docs:templates.docCreated'));
    }
    return Boolean(success && response?.uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData, folderUuid]);

  const handleCreateFromTemplate = useCallback(async (template, language = null) => {
    setTemplatesModalOpen(false);
    if (!template?.uuid) return;
    if (template.templateEntity) {
      setFillingTemplate({ template, language });
      return;
    }
    await instantiateTemplate(template, language);
  }, [instantiateTemplate]);

  const handleFilledFromRecord = useCallback(async ({ values, sourceRef }) => {
    if (!fillingTemplate) return;
    const extra = {
      ...(values.length ? { values } : {}),
      ...(sourceRef ? { sourceRef } : {}),
    };
    if (await instantiateTemplate(fillingTemplate.template, fillingTemplate.language, extra)) {
      setFillingTemplate(null);
    }
  }, [fillingTemplate, instantiateTemplate]);

  // A new search starts from page 1. We DON'T clear accFiles here — the accum
  // key change (search is in it) replaces the buffer when page 1 lands, so the
  // rows swap in place rather than unmounting to empty (avoids the teardown).
  useEffect(() => {
    setFilesPage(1);
  }, [debouncedSearch, setFilesPage]);


  // Execute search when debounced value changes
  const isSearching = debouncedSearch.trim() !== '';


  // Handle different response structures. The standard tabs get a MIXED
  // (folders + files) page from /node/children, each item carrying `type`.
  const files = useMemo(() => {
    const response = loadFiles.response;
    if (!response) return [];
    // Already mixed and type-tagged. The archived tab serves files only —
    // tag them for the shared renderers.
    return (response.items || []).map((i) => (i.type ? i : { ...i, type: 'file' }));
  }, [loadFiles.response]);

  // Restart paging whenever the query context changes (filter / folder / sort):
  // reset to page 1 and clear the accumulated buffer so the new context replaces.
  useEffect(() => {
    setFilesPage(1);
    setAccFiles([]);
    accKeyRef.current = '';
    setShowScrollTop(false);
    lastScrollTopRef.current = 0;
  }, [activeFilter, typeFilter, visFilter, actionOnly, folderUuid, sortBy, sortDir, setFilesPage]);

  // Accumulate file pages for infinite scroll. Uses the server's own `page`
  // marker (not the local state, which lags a tick) to decide replace-vs-append,
  // and a context key so a filter/sort switch replaces instead of appending.
  useEffect(() => {
    const resp = loadFiles.response;
    if (!resp) return;
    // useLoad clears the response to `[]` on every dependency change (e.g. when
    // filesPage bumps for the next page). Ignore that transient empty-array
    // sentinel, otherwise it would wipe the buffer right before the new page
    // appends to nothing. Real empty results come back as `{items: []}`.
    if (Array.isArray(resp) && resp.length === 0) return;
    const key = `${activeFilter}|${typeFilter}|${visFilter}|${actionOnly}|${folderUuid || ''}|${sortBy}|${sortDir}|${debouncedSearch}`;
    const respPage = Array.isArray(resp) ? 1 : (resp.page || 1);
    setAccFiles((prev) => {
      if (accKeyRef.current !== key || respPage === 1) return files;
      // Dedup only the incoming page against uuids already buffered — O(page)
      // per append, vs uniqBy rescanning the whole growing list each time.
      const have = new Set(prev.map((f) => f.uuid));
      return [...prev, ...files.filter((f) => !have.has(f.uuid))];
    });
    accKeyRef.current = key;
    // Only paginated responses support infinite scroll; anything without
    // page metadata stays single-page.
    const pages = Array.isArray(resp) ? null : resp.pages;
    setFilesHasMore(typeof pages === 'number' ? respPage < pages : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFiles.response]);

  // Fetch the next page when the scroll container nears the bottom, and drive
  // the direction-aware "scroll to top" button: it appears only while scrolling
  // UP (and once you're well down the list), and hides on the way down or near
  // the top. Direction lives in a ref so tracking it doesn't re-render.
  const lastScrollTopRef = useRef(0);
  const handleContentScroll = (e) => {
    const el = e.currentTarget;
    if (filesHasMore && !loadFiles.loading && el.scrollHeight - el.scrollTop - el.clientHeight < 320) {
      setFilesPage((p) => p + 1);
    }
    const y = el.scrollTop;
    const prev = lastScrollTopRef.current;
    if (y < prev - 4 && y > 600) setShowScrollTop(true);        // scrolling up, not near top
    else if (y > prev + 4 || y < 200) setShowScrollTop(false);  // scrolling down, or near top
    lastScrollTopRef.current = y;
  };

  const scrollToTop = () => {
    listContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setShowScrollTop(false);
  };

  // Context menu shared by the Shared overlay's card and list rows.
  const sharedContextMenu = (item) => (
    <ContextMenuContent className="w-48">
      <ContextMenuItem
        onClick={() => openProps(item.data.node, item.kind)}
        className="cursor-pointer"
      >
        <Info className="mr-2 h-4 w-4" />
        {t('my-docs:properties.title') || 'Properties'}
      </ContextMenuItem>
      {sharedMode === 'received' && canHandOver(item.data.node) && (
        <ContextMenuItem
          onClick={() => setHandingOver({ ...item.data.node, type: item.kind })}
          className="cursor-pointer"
        >
          <UserCog className="mr-2 h-4 w-4" />
          {t('my-docs:owner.menu')}
        </ContextMenuItem>
      )}
      {sharedMode === 'sent' && (
        <ContextMenuItem
          onClick={() => { setSelectedShareItem(item.data); setSharedDeleteOpen(true); }}
          className="text-red-600 focus:text-red-600 cursor-pointer"
        >
          <Trash2 className="mr-2 h-4 w-4 text-red-600" />
          {t('shareModal:remove.btn') || 'Remove share'}
        </ContextMenuItem>
      )}
    </ContextMenuContent>
  );

  // The server interleaves folders before files and paginates across both,
  // so the accumulated buffer (accFiles) IS the display list — no client-side
  // merge, no filtering, so it's rendered directly.


  const [selectedFolder, setSelectedFolder] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [shareFolderOpen, setShareFolderOpen] = useState(false);
  // the file or folder being handed over to someone else
  const [handingOver, setHandingOver] = useState(null);
  // "Allow the assistant to read this": the nodes the dialog is open for, or
  // null. Offered where docs-api issues grants, on what the person may share.
  const [assistantNodes, setAssistantNodes] = useState(null);
  const grantsOn = useGrantsAvailable();
  // What the person's scopes allow at all (docs/ACCESS.md); each node's own
  // `access.can` narrows it further.
  const mayDelete = iHold(SCOPE.FILES_DELETE);
  // Creating (upload, folders, documents, copies) is files:write's.
  const mayCopy = iHold(SCOPE.FILES_WRITE);
  const mayMove = iHold(SCOPE.FILES_UPDATE);
  const maySend = iHold(SCOPE.SIGNING_WRITE);

  // Helper to check if any modal is active
  const isAnyModalOpen = useMemo(() => {
    return (
      open || deleteOpen || updateOpen || renameOpen || shareFolderOpen ||
      deleteFileOpen || shareFileOpen || visibilityOpen ||
      versionHistoryOpen || docTypeOpen ||
      activityOpen || commentsOpen || editorOpen ||
      templatesModalOpen ||
      !!document.querySelector('[role="dialog"]')
    );
  }, [
    open, deleteOpen, updateOpen, renameOpen, shareFolderOpen,
    deleteFileOpen, shareFileOpen, visibilityOpen,
    versionHistoryOpen, docTypeOpen,
    activityOpen, commentsOpen, editorOpen,
    templatesModalOpen
  ]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      if (e.target.closest('[role="dialog"]')) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Global drag-and-drop handlers ---
  // Whole-window upload target. Files are held, not uploaded: the create
  // modal opens so the user can pick a type and visibility first; it checks
  // them against the install's upload limits (name, size, type).
  const { isDragOver, handlers: windowDropHandlers } = useFileDrop({
    // An upload is files:write's: without it, a dropped file opens nothing.
    disabled: isAnyModalOpen || !iHold(SCOPE.FILES_WRITE),
    onFiles: (accepted) => {
      setDraggedFiles(accepted);
      setOpen(true);
    },
  });

  // --- Internal drag-and-drop for moving files ---
  // A row dragged onto a folder (or a crumb) moves there: the file's uuid
  // and the folder it came from ride along in the drag.
  const handleInternalDragStart = (e, file) => {
    e.dataTransfer.setData('fileUuid', file.uuid);
    e.dataTransfer.setData('sourceFolderUuid', parentOf(file) ?? '');
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleFolderDragOver = (e, targetFolderUuid) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedOverFolderUuid !== targetFolderUuid) {
      setDraggedOverFolderUuid(targetFolderUuid);
    }
    e.dataTransfer.dropEffect = 'move';
  };

  const handleFolderDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverFolderUuid(undefined);
  };

  const handleInternalDropToFolder = async (e, targetFolderUuid) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggedOverFolderUuid(undefined);

    const fileUuid = e.dataTransfer.getData('fileUuid');
    if (!fileUuid) return;

    // Don't move if dropped in the same folder (null: the root)
    const sourceFolderUuid = e.dataTransfer.getData('sourceFolderUuid');
    if (sourceFolderUuid === (targetFolderUuid ?? '')) return;

    const { success } = await moveFile.request({
      url: FILE.replace('{file_uuid}', fileUuid),
      data: { parentUuid: targetFolderUuid ?? null },
    });

    if (success) {
      toast.success(t('my-docs:move.success') || 'File moved successfully');
      refreshData();
    }
  };

  // Attach global listeners to window
  useEffect(() => {
    const { onDragEnter, onDragLeave, onDragOver, onDrop } = windowDropHandlers;
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [windowDropHandlers]);

  // Secondary row — refines My Documents only, split into three INDEPENDENT
  // facets so they compose (the old flat strip conflated Type/Visibility/Status
  // into one single-select). Archive & Shared are LOCATION tabs, not filters.
  // Clear every facet back to "off" — used by the active-filter summary line.
  const facetsActive = typeFilter !== 'all' || visFilter !== 'any' || actionOnly;
  const clearFacets = () => { setTypeFilter('all'); setVisFilter('any'); setActionOnly(false); };

  // Primary navigation — locations (each its own dataset + count).
  const primaryTabs = [
    { value: 'my_documents', label: t('mainPage:locations.myDocuments') || 'My Documents', icon: <Folder className="w-4 h-4" />, count: myDocsCount || 0, onClick: goMyDocuments },
    { value: 'shared', label: t('mainPage:locations.shared') || 'Shared', icon: <Share2 className="w-4 h-4" />, count: counts.shared || 0, onClick: () => setSharedView(true) },
    { value: 'archive', label: t('mainPage:locations.archive') || 'Archive', icon: <Archive className="w-4 h-4" />, count: counts.archived || 0, onClick: () => goLocation('archived') },
    { value: 'trash', label: t('mainPage:locations.trash') || 'Trash', icon: <Trash2 className="w-4 h-4" />, count: counts.trash || 0, onClick: () => goLocation('trash') },
    // The template library — also a route change; templates never sit in the tree.
    { value: 'templates', label: t('mainPage:locations.templates'), icon: <LayoutGrid className="w-4 h-4" />, onClick: () => navigate('/templates') },
    // The departmental archive — only where it is on and this person keeps or reads it.
    ...(showsRecords(recordsAccess)
      ? [{ value: 'records', label: t('records:title'), icon: <Library className="w-4 h-4" />, onClick: () => navigate('/records') }]
      : []),
  ];

  // The single create menu, rendered by BOTH the "+ New" dropdown and the
  // right-click-empty-canvas context menu (same items, two doors). `menu`
  // supplies the matching primitives so the dropdown and context menus stay in
  // lock-step. Only meaningful in My Documents (gated at the call sites).
  const renderCreateMenuItems = (menu) => {
    const Item = menu.Item;
    const Separator = menu.Separator;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
    return (
      <>
        <Item className="cursor-pointer" onClick={stop(() => setOpen(true))}>
          <UploadIcon className="mr-2 h-4 w-4" /> {t('mainPage:create.upload') || 'Upload'}
        </Item>
        <Item className="cursor-pointer" onClick={stop(() => setFolderCreateOpen(true))}>
          <FolderLucide className="mr-2 h-4 w-4" /> {t('mainPage:create.folder') || 'New folder'}
        </Item>
        <Separator />
        <Item className="cursor-pointer" disabled={isCreatingBlank} onClick={stop(handleCreateBlankDoc)}>
          <FilePlus className="mr-2 h-4 w-4" /> {t('mainPage:create.blank') || 'Blank document'}
        </Item>
        <Item className="cursor-pointer" onClick={stop(() => setTemplatesModalOpen(true))}>
          <LayoutGrid className="mr-2 h-4 w-4" /> {t('mainPage:create.template') || 'From template…'}
        </Item>
      </>
    );
  };

  // Plain-DOM menu primitives so renderCreateMenuItems can also drive the
  // right-click-empty-canvas menu (Radix DropdownMenuItem/Separator shapes).
  // Radix closes its own menu on select; the canvas one has to be told.
  const canvasMenuPrimitives = useMemo(
    () => ({
      Item: (props) => <CanvasMenuItem {...props} onSelect={() => setCreateMenu(null)} />,
      Separator: CanvasMenuSeparator,
    }),
    [],
  );

  // Open the create menu when the right-click lands on empty list space (not a
  // row — rows keep their own context menu). My Documents only; nothing to
  // create inside Shared/Archive/Trash or the shared overlay.
  const handleCanvasContextMenu = (e) => {
    if (navLocation !== 'my_documents' || sharedView || !mayCopy) return;
    if (e.target.closest('[data-row-id]') || e.target.closest('a')) return;
    e.preventDefault();
    // Clamp so the ~200px menu never opens off-screen (narrow viewports,
    // right-edge clicks on desktop).
    setCreateMenu({
      x: Math.min(e.clientX, Math.max(window.innerWidth - 216, 8)),
      y: Math.min(e.clientY, Math.max(window.innerHeight - 220, 8)),
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // The in-app editor (Collabora) only handles office/text formats. Images,
  // PDFs and other binaries can't be edited, so the Edit action is hidden for
  // them.
  const NON_EDITABLE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tif', 'tiff', 'heic', 'pdf', 'zip', 'rar', '7z', 'mp4', 'mov', 'mp3', 'wav'];
  const isEditableFile = (item) => {
    const name = item.name || '';
    const ext = (name.includes('.') ? name.split('.').pop() : (item.extension || '')).toLowerCase();
    return !NON_EDITABLE_EXTS.includes(ext);
  };

  // `name` is the full display name — extension included for files,
  // plain folder name for folders.
  const fileDisplayName = (item) => item.name || '';

  // Single source of truth for a file's action menu. Rendered identically by
  // the list-view dropdown ("⋯"), the list-view right-click menu and the
  // card-view right-click menu — pass in the matching primitives so all three
  // stay in lock-step instead of drifting apart.
  const renderFileMenuItems = (item, menu) => {
    const Item = menu.Item;
    const Separator = menu.Separator;
    const isFileOwner = isOwner(item);
    // Editor level or above — `can_edit` also turns false while the file
    // is locked, signed or filed, which says nothing about the person.
    const hasWriteAccess = atLeast(item, 'editor');
    const itemType = item.approvalState || item.approval_state;
    const typeLocked = itemType === 'signed'; // signed is terminal
    const itemArchived = Boolean(item.archivedAt);
    // A filed record keeps its bytes, name and place for good.
    const isRecord = Boolean(item.filedAt);
    // A locked file can't be renamed or deleted until it is unlocked.
    const isLocked = editBlock(item) === 'locked';
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
    return (
      <>
        {can(item, 'share') && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setShareFileOpen(true); })}>
            <Share2 className="mr-2 h-4 w-4" /> {t('my-docs:actions.share')}
          </Item>
        )}
        {grantsOn && mayAllow([item]) && (
          <Item className="cursor-pointer" onClick={stop(() => setAssistantNodes([item]))} data-testid="menu-allow-assistant">
            <Bot className="mr-2 h-4 w-4" /> {t('assistant:grants.menu')}
          </Item>
        )}
        {can(item, 'move') && !isRecord && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setMoveFileOpen(true); })}>
            <Folder className="mr-2 h-4 w-4" /> {t('my-docs:actions.move') || 'Переместить'}
          </Item>
        )}
        {can(item, 'rename') && !isRecord && !isLocked && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setRenameOpen(true); })}>
            <PenLine className="mr-2 h-4 w-4" /> {t('my-docs:actions.rename') || 'Rename'}
          </Item>
        )}
        {typeLocked && (
          <Item className="cursor-pointer" onClick={stop(() => { setPreviewDoc(item); })}>
            <PenTool className="mr-2 h-4 w-4 text-purple-600" /> {t('my-docs:actions.sign') || 'Sign'}
          </Item>
        )}
        {can(item, 'visibility') && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setVisibilityOpen(true); })}>
            <Eye className="mr-2 h-4 w-4" /> {t('my-docs:actions.visibility')}
          </Item>
        )}

        {(isFileOwner || hasWriteAccess) && <Separator />}
        {canEditFile(item) && isEditableFile(item) && (
          <Item className="cursor-pointer" onClick={stop(() => handleRequestEdit(item))}>
            <Pencil className="mr-2 h-4 w-4" /> {t('my-docs:actions.edit')}
          </Item>
        )}
        {canLockFile(item) && (
          <Item className="cursor-pointer" onClick={stop(() => setLocking({ file: item, mode: 'lock' }))} data-testid="menu-lock">
            <LockKeyhole className="mr-2 h-4 w-4" /> {t('my-docs:lock.menu')}
          </Item>
        )}
        {canUnlockFile(item) && (
          <Item className="cursor-pointer" onClick={stop(() => setLocking({ file: item, mode: 'unlock' }))} data-testid="menu-unlock">
            <LockKeyholeOpen className="mr-2 h-4 w-4" /> {t('my-docs:lock.unlockMenu')}
          </Item>
        )}
        {mayCopy && (
          <Item className="cursor-pointer" onClick={stop(() => handleDuplicateFile(item))} disabled={cloneFile.loading}>
            <Copy className="mr-2 h-4 w-4" /> {t('my-docs:actions.duplicate')}
          </Item>
        )}
        {/* No manual upload-version: editor saves are versioned automatically. */}
        {isFileOwner && <Separator />}
        <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setVersionHistoryOpen(true); })}>
          <History className="mr-2 h-4 w-4" /> {t('my-docs:actions.history')}
        </Item>
        {isFileOwner && maySend && (
          <>
            <Separator />
            <Item
              disabled={typeLocked}
              className={typeLocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              onClick={stop(() => { if (!typeLocked) { setSelectedFile(item); setDocTypeOpen(true); } })}
              title={typeLocked ? (t('my-docs:docType.lockedHint') || 'Signed documents are terminal. Clone the file to start over.') : undefined}
            >
              <FileType className="mr-2 h-4 w-4" /> {t('my-docs:actions.changeType')}
            </Item>
          </>
        )}
        <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setActivityOpen(true); })}>
          <Activity className="mr-2 h-4 w-4" /> {t('my-docs:actions.activity')}
        </Item>
        <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setCommentsOpen(true); })}>
          <MessageSquare className="mr-2 h-4 w-4" /> {t('my-docs:actions.comments')}
        </Item>
        {canHandOver(item) && (
          <Item className="cursor-pointer" onClick={stop(() => setHandingOver(item))} data-testid="menu-transfer-owner">
            <UserCog className="mr-2 h-4 w-4" /> {t('my-docs:owner.menu')}
          </Item>
        )}
        <Item className="cursor-pointer" onClick={stop(() => openProps(item, (itemType && itemType !== 'ordinary') ? 'document' : 'file'))}>
          <Info className="mr-2 h-4 w-4" /> {t('my-docs:properties.title') || 'Properties'}
        </Item>
        {canRegister(recordsAccess) && !itemArchived && (
          <Item className="cursor-pointer" onClick={stop(() => setRegistering(item))} data-testid="menu-register">
            <BookMarked className="mr-2 h-4 w-4" /> {t('records:register.menu')}
          </Item>
        )}
        {canFileNode(recordsAccess, item, { canEdit: Boolean(hasWriteAccess) }) && (
          <Item className="cursor-pointer" onClick={stop(() => setFiling({ node: item, mode: 'file' }))} data-testid="menu-file-to-case">
            <Lock className="mr-2 h-4 w-4" /> {t('records:filing.menu')}
          </Item>
        )}
        {isFileOwner && mayDelete && !isRecord && !(isLocked && !itemArchived && !typeLocked) && (
          <>
            <Separator />
            {itemArchived ? (
              <Item className="cursor-pointer" onClick={stop(() => handleOpenRestore(item))}>
                <ArchiveRestore className="mr-2 h-4 w-4" /> {t('my-docs:contextMenu.restore') || 'Restore'}
              </Item>
            ) : typeLocked ? (
              <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setArchiveOpen(true); })}>
                <Archive className="mr-2 h-4 w-4" /> {t('my-docs:contextMenu.archive') || 'Archive'}
              </Item>
            ) : (
              <Item className="cursor-pointer text-red-600 focus:text-red-600" onClick={stop(() => { setSelectedFile(item); setDeleteFileOpen(true); })}>
                <Trash2 className="mr-2 h-4 w-4" /> {t('my-docs:btn.delete')}
              </Item>
            )}
          </>
        )}
      </>
    );
  };

  // Bulk action menu — shown on right-click when several rows are selected, so
  // the menu reflects the whole selection instead of one row's single-file
  // actions. Mirrors the floating toolbar (Move/Copy/Delete) so the two surfaces
  // stay in lock-step — Delete's confirm dialog archives any signed docs, so
  // there's no separate Archive item here either.
  const renderBulkMenuItems = (menu) => {
    const Item = menu.Item;
    const Separator = menu.Separator;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
    return (
      <>
        {mayMove && (
          <Item className="cursor-pointer" onClick={stop(() => openBulkMove(false))}>
            <Folder className="mr-2 h-4 w-4" /> {t('my-docs:actions.move') || 'Move'}
          </Item>
        )}
        {mayCopy && (
          <Item
            disabled={hasFolderSelection}
            className={hasFolderSelection ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            onClick={stop(() => { if (!hasFolderSelection) openBulkMove(true); })}
            title={hasFolderSelection ? (t('my-docs:bulk.copyFoldersUnsupported') || "Folders can't be copied yet") : undefined}
          >
            <Copy className="mr-2 h-4 w-4" /> {t('my-docs:actions.copy') || 'Copy'}
          </Item>
        )}
        {grantsOn && mayAllow(selectedNodes) && (
          <Item className="cursor-pointer" onClick={stop(() => setAssistantNodes(selectedNodes))} data-testid="bulk-menu-allow-assistant">
            <Bot className="mr-2 h-4 w-4" /> {t('assistant:grants.bulk')}
          </Item>
        )}
        {mayDelete && (
          <>
            <Separator />
            <Item className="cursor-pointer text-red-600 focus:text-red-600" onClick={stop(() => setBulkDeleteOpen(true))}>
              <Trash2 className="mr-2 h-4 w-4" /> {t('my-docs:bulk.delete') || 'Delete'}
            </Item>
          </>
        )}
      </>
    );
  };

  // Single source for a FOLDER's action menu — rendered identically by the
  // list/card right-click menus and the kebab so they can't drift (the kebab
  // used to silently miss Move + Properties). Mirrors renderFileMenuItems; the
  // trash branch at each call site swaps in renderTrashMenuItems just like files.
  const renderFolderMenuItems = (item, menu) => {
    const Item = menu.Item;
    const Separator = menu.Separator;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
    return (
      <>
        {can(item, 'share') && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFolder(item); setShareFolderOpen(true); })}>
            <Share2 className="mr-2 h-4 w-4" /> {t('my-docs:actions.share')}
          </Item>
        )}
        {grantsOn && mayAllow([item]) && (
          <Item className="cursor-pointer" onClick={stop(() => setAssistantNodes([item]))} data-testid="menu-allow-assistant">
            <Bot className="mr-2 h-4 w-4" /> {t('assistant:grants.menu')}
          </Item>
        )}
        {canFileNode(recordsAccess, item, { canEdit: can(item, 'edit') }) && (
          <Item className="cursor-pointer" onClick={stop(() => setFiling({ node: item, mode: 'folder' }))} data-testid="menu-file-folder">
            <Lock className="mr-2 h-4 w-4" /> {t('records:filing.folderMenu')}
          </Item>
        )}
        {recordsAccess?.enabled && recordsAccess?.can_manage && (
          <Item className="cursor-pointer" onClick={stop(() => setFiling({ node: item, mode: 'link' }))} data-testid="menu-link-case">
            <BookMarked className="mr-2 h-4 w-4" /> {t('records:filing.linkMenu')}
          </Item>
        )}
        {can(item, 'move') && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFile(item); setMoveFileOpen(true); })}>
            <Folder className="mr-2 h-4 w-4" /> {t('my-docs:actions.move') || 'Move'}
          </Item>
        )}
        {can(item, 'rename') && (
          <Item className="cursor-pointer" onClick={stop(() => { setSelectedFolder(item); setUpdateOpen(true); })}>
            <Pencil className="mr-2 h-4 w-4" /> {t('my-docs:btn.update')}
          </Item>
        )}
        {canHandOver(item) && (
          <Item className="cursor-pointer" onClick={stop(() => setHandingOver(item))} data-testid="menu-transfer-owner">
            <UserCog className="mr-2 h-4 w-4" /> {t('my-docs:owner.menu')}
          </Item>
        )}
        <Item className="cursor-pointer" onClick={stop(() => openProps(item, 'folder'))}>
          <Info className="mr-2 h-4 w-4" /> {t('my-docs:properties.title') || 'Properties'}
        </Item>
        {can(item, 'delete') && (
          <>
            <Separator />
            <Item className="cursor-pointer text-red-600 focus:text-red-600" onClick={stop(() => { setSelectedFolder(item); setDeleteOpen(true); })}>
              <Trash2 className="mr-2 h-4 w-4 text-red-600" /> {t('my-docs:btn.delete')}
            </Item>
          </>
        )}
      </>
    );
  };

  // Trash menu: nothing is permanently deleted, so the only real action is
  // Restore. Editing/moving/sharing are disabled (they 404 on a trashed node
  // anyway) with a "restore first" hint; Properties stays available.
  const renderTrashMenuItems = (item, menu) => {
    const Item = menu.Item;
    const Separator = menu.Separator;
    const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
    const hint = t('my-docs:trash.restoreFirst') || 'Restore the item to do this';
    const itemType = item.approvalState || item.approval_state;
    const dis = (icon, label) => (
      <Item disabled className="cursor-not-allowed opacity-50" title={hint}>
        {icon} {label}
      </Item>
    );
    return (
      <>
        {mayDelete && (
          <>
            <Item className="cursor-pointer text-green-700 focus:text-green-700" onClick={stop(() => restoreFromTrash(item))}>
              <ArchiveRestore className="mr-2 h-4 w-4" /> {t('my-docs:trash.restore') || 'Restore'}
            </Item>
            <Separator />
          </>
        )}
        {dis(<Share2 className="mr-2 h-4 w-4" />, t('my-docs:actions.share'))}
        {dis(<Folder className="mr-2 h-4 w-4" />, t('my-docs:actions.move') || 'Move to folder')}
        {dis(<PenLine className="mr-2 h-4 w-4" />, t('my-docs:actions.rename') || 'Rename')}
        {dis(<Eye className="mr-2 h-4 w-4" />, t('my-docs:actions.visibility'))}
        {dis(<Copy className="mr-2 h-4 w-4" />, t('my-docs:actions.duplicate'))}
        {dis(<History className="mr-2 h-4 w-4" />, t('my-docs:actions.history'))}
        {dis(<FileType className="mr-2 h-4 w-4" />, t('my-docs:actions.changeType'))}
        {dis(<Activity className="mr-2 h-4 w-4" />, t('my-docs:actions.activity'))}
        {dis(<MessageSquare className="mr-2 h-4 w-4" />, t('my-docs:actions.comments'))}
        <Separator />
        <Item className="cursor-pointer" onClick={stop(() => openProps(item, (itemType && itemType !== 'ordinary') ? 'document' : (item.type === 'folder' ? 'folder' : 'file')))}>
          <Info className="mr-2 h-4 w-4" /> {t('my-docs:properties.title') || 'Properties'}
        </Item>
        {/* Hard delete — the manual cleanup exit from the trash (irreversible). */}
        {mayDelete && (
          <>
            <Separator />
            <Item className="cursor-pointer text-red-600 focus:text-red-600" onClick={stop(() => askPurge([item.uuid]))}>
              <Trash2 className="mr-2 h-4 w-4" /> {t('my-docs:trash.deleteForever') || 'Delete permanently'}
            </Item>
          </>
        )}
      </>
    );
  };

  /**
   * Which menu a right-click gets. Card and list view each render this for
   * folders and for files, so the rule lived in four places and a change to it
   * meant four edits; it is stated once here.
   *
   * Note the asymmetry, which is existing behaviour and NOT a refactor: a file
   * inside a multi-selection gets the bulk menu, a folder does not. Right-click
   * a selected folder and you act on that folder alone.
   */
  const nodeMenuItems = (item) => {
    const menu = { Item: ContextMenuItem, Separator: ContextMenuSeparator };
    if (navLocation === 'trash') return renderTrashMenuItems(item, menu);
    if (item.type === 'folder') return renderFolderMenuItems(item, menu);
    if (selectionCount > 1 && selectedUuids.has(item.uuid)) return renderBulkMenuItems(menu);
    return renderFileMenuItems(item, menu);
  };

  // Humanized "modified" label (Finder-style): "Today, 9:10 AM", "Yesterday",
  // "3 days ago", "Last week", "2 weeks ago", "Last month", "4 months ago"…
  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const now = new Date();
    const days = Math.floor((now - d) / 86400000);
    const sameDay = d.toDateString() === now.toDateString();

    if (sameDay) {
      const h = d.getHours();
      const ampm = h >= 12 ? 'PM' : 'AM';
      const time = `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
      return t('my-docs:time.today', { time });
    }
    if (days <= 1) return t('my-docs:time.yesterday');
    if (days < 7) return t('my-docs:time.daysAgo', { count: days });
    if (days < 14) return t('my-docs:time.lastWeek');
    if (days < 30) return t('my-docs:time.weeksAgo', { count: Math.floor(days / 7) });
    if (days < 60) return t('my-docs:time.lastMonth');
    if (days < 365) return t('my-docs:time.monthsAgo', { count: Math.floor(days / 30) });
    if (days < 730) return t('my-docs:time.lastYear');
    return t('my-docs:time.yearsAgo', { count: Math.floor(days / 365) });
  };

  return (
    // h-full, not h-screen: this sits BELOW TopNavigation inside Layout's
    // height-clamped column — a viewport-height root here made the page
    // TopNav+100vh tall and clipped the bottom rows in the tc-mall iframe.
    <div className="flex flex-col h-full overflow-hidden bg-background">
      <DragOverlay visible={isDragOver} />
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="px-3 py-3 md:px-6 md:py-4">
          {/* Row 1 — LOCATION tabs (left, the page anchor) + create actions
              (right). The tabs replace the old "My Documents" title; the folder
              path moved to its own slim row below. */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 mb-4">
            <LocationTabs
              tabs={primaryTabs}
              activeValue={navLocation}
              className="inline-flex gap-1 rounded-[4px] bg-muted/60 p-1 max-md:max-w-full scrollbar-hide"
            />

            {/* Create actions — only in My Documents (you can't create into
                Shared/Archive/Trash). E-IMZO is in the top bar on every page. */}
            <div className="flex items-center gap-2" ref={dropdownRef}>
              {navLocation === 'my_documents' && mayCopy && (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="gap-1.5 rounded-[4px] bg-brand hover:bg-[#324E8C] text-white">
                        {isCreatingBlank ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {t('mainPage:create.new') || 'New'}
                        <ChevronDown className="w-3.5 h-3.5 opacity-80" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 rounded-[4px]">
                      {renderCreateMenuItems({ Item: DropdownMenuItem, Separator: DropdownMenuSeparator })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              {/* Modals (no inline triggers): folder-create runs controlled from
                  the "+ New" menu; file upload is opened via setOpen / drag-drop. */}
              <Create
                setData={refreshData}
                parentFolderUuid={folderUuid}
                open={folderCreateOpen}
                onOpenChange={setFolderCreateOpen}
                hideTrigger
              />
              <FileCreate
                setData={loadFiles.request}
                open={open}
                onOpenChange={(val) => {
                  setOpen(val);
                  if (!val) setDraggedFiles(null);
                }}
                folderUuid={folderUuid}
                initialFiles={draggedFiles}
                hideTrigger
              />
            </div>
          </div>

          {navLocation === 'my_documents' && folderUuid && (
            <FolderPathBar
              history={navigationHistory}
              currentIndex={currentHistoryIndex}
              onNavigate={goToHistoryIndex}
              draggedOverFolderUuid={draggedOverFolderUuid}
              onFolderDragOver={handleFolderDragOver}
              onFolderDragLeave={handleFolderDragLeave}
              onFolderDrop={handleInternalDropToFolder}
            />
          )}

          <ListingToolbar
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onClearSearch={clearSearch}
            folderName={currentFolder?.name}
            inFolder={Boolean(folderUuid)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />

          {navLocation === 'my_documents' && actionBannerVisible && (
            <ActionRequiredBanner count={actionCount} onShowOnly={() => setActionOnly(true)} />
          )}

          {/* Secondary row — three INDEPENDENT facets that AND together (Type ·
              Visibility · Status), so "Signed + Private + Action required" is
              expressible. Refines My Documents only. */}
          {navLocation === 'my_documents' && (
            <FacetBar
              typeFilter={typeFilter}
              onTypeChange={setTypeFilter}
              visFilter={visFilter}
              onVisibilityChange={setVisFilter}
              actionOnly={actionOnly}
              onActionToggle={() => setActionOnly((v) => !v)}
              actionCount={actionCount}
              hasActiveFacets={facetsActive}
              onClear={clearFacets}
            />
          )}
          {navLocation === 'shared' && (
            <div className="flex items-center gap-1.5">
              {[
                { value: 'received', label: t('shared:btn.received') },
                { value: 'sent', label: t('shared:btn.sent') },
              ].map((tab) => {
                const active = sharedMode === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => setSharedMode(tab.value)}
                    className={cn(
                      'relative px-3 py-1 rounded-[4px] text-[13px] font-medium transition-colors cursor-pointer',
                      active ? 'text-white' : 'text-muted-foreground hover:bg-muted/70'
                    )}
                  >
                    {active && (
                      <TabPill
                        layoutId="shared-subtab-pill"
                        transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                        className="absolute inset-0 rounded-[4px] bg-brand"
                      />
                    )}
                    <span className="relative z-10">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 min-h-0 flex flex-col p-3 pb-1 md:p-6 md:pb-1" onContextMenu={handleCanvasContextMenu}>
        {sharedView ? (
          sharedLoading ? (
            <CardsSkeleton />
          ) : sharedItems.length > 0 ? (
            viewMode === 'cards' ? (
              <div
                key={`shared-${sharedMode}`}
                className={`grid grid-cols-[repeat(auto-fill,135px)] gap-x-6 gap-y-8 justify-between max-md:justify-evenly content-start fade-slide-in ${scrollBoxClass}`}
                onScroll={handleContentScroll}
              >
                {sharedItems.map((item) => (
                  <ContextMenu key={item.key}>
                    <ContextMenuTrigger>
                      {item.kind === 'folder'
                        ? <SharedFolder share={item.data} />
                        : <SharedDocument file={item.data} owner={sharedMode === 'sent'} />}
                    </ContextMenuTrigger>
                    {sharedContextMenu(item)}
                  </ContextMenu>
                ))}
              </div>
            ) : (
              <div key={`shared-list-${sharedMode}`} className="flex-1 min-h-0 flex flex-col fade-slide-in">
                <div className="hidden sm:flex items-center gap-3 px-2 pb-1.5 mb-1 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <div className="w-10 shrink-0" />
                  <div className="flex-1 min-w-0">{t('mainPage:columns.name') || 'Name'}</div>
                  <div className="w-20 text-right shrink-0">{t('shared:columns.access') || 'Access'}</div>
                  <div className="w-24 text-right shrink-0">{t('mainPage:columns.size') || 'Size'}</div>
                  <div className="w-32 text-right shrink-0">{t('mainPage:columns.modified') || 'Modified'}</div>
                </div>
                <ul className={`flex flex-col gap-1 ${scrollBoxClass}`} onScroll={handleContentScroll}>
                {sharedItems.map((item) => {
                  const isFolder = item.kind === 'folder';
                  const obj = item.data.node;
                  // Received shares carry the level; older payloads only the
                  // legacy permission (a viewer read and commented).
                  const perm = item.data.level || (item.data.permission === 'editor' ? 'editor' : item.data.permission ? 'commenter' : null);
                  const name = obj?.name;
                  const sizeText = isFolder ? `${obj?.itemCount ?? obj?.item_count ?? 0}` : formatFileSize(obj?.size);
                  const modified = formatRelativeDate(obj?.updatedAt || obj?.updated_at || obj?.createdAt || obj?.created_at);
                  const rowInner = (
                    <>
                      <div className="w-10 shrink-0 flex justify-center">
                        {isFolder
                          ? <img src="/images/folder.svg" alt="" className="w-7 h-7 object-contain" />
                          : <FileTypeIcon file={obj} className="w-6 h-6" />}
                      </div>
                      <div className="flex-1 min-w-0 truncate text-sm text-foreground">{name}</div>
                      <div className="w-20 text-right shrink-0">
                        {perm && (
                          <span className={cn(
                            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase',
                            ['editor', 'manager', 'contributor'].includes(perm) ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70'
                          )}>
                            {t(`shareModal:access.level.${perm}.name`)}
                          </span>
                        )}
                      </div>
                      <div className="w-24 text-right shrink-0 text-sm text-muted-foreground">{sizeText}</div>
                      <div className="w-32 text-right shrink-0 text-sm text-muted-foreground">{modified}</div>
                    </>
                  );
                  return (
                    <li key={item.key}>
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        {isFolder ? (
                          <Link to={`/files/${obj?.uuid}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors cursor-pointer">
                            {rowInner}
                          </Link>
                        ) : (
                          <div
                            className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => setPreviewDoc(obj)}
                          >
                            {rowInner}
                          </div>
                        )}
                      </ContextMenuTrigger>
                      {sharedContextMenu(item)}
                    </ContextMenu>
                    </li>
                  );
                })}
                </ul>
              </div>
            )
          ) : (
            <div className="w-full flex items-center justify-center text-gray-500 h-40">
              {sharedMode === 'sent' ? t('shared:empty.sent') : t('shared:empty.received')}
            </div>
          )
        ) : loadFiles.loading && accFiles.length === 0 ? (
          // Skeleton grid only on the initial load — subsequent infinite-scroll
          // page fetches keep the accumulated list visible and show the bottom
          // "loading more" row instead.
          <CardsSkeleton />
        ) : accFiles.length > 0 ? (
          /* Folders/files view — also renders search results, since search is
             now a filter on this same list (recursive within the folder). */
          viewMode === 'cards' ? (
            // Card View - EXACT DESIGN FROM PHOTO
            // auto-fill tracks + justify-between: leftover width that can't fit
            // another 135px card is distributed between columns instead of
            // piling up as a gap on the right edge. Sparse last rows still
            // align to the same tracks.
            <div ref={listContainerRef} tabIndex={0} onKeyDown={handleListKeyDown} onMouseDown={handleListBackgroundMouseDown} key={`cards-${activeFilter}`} className={`grid grid-cols-[repeat(auto-fill,135px)] justify-between max-md:justify-evenly gap-x-4 gap-y-4 content-start px-2 pt-2 fade-slide-in outline-none ${scrollBoxClass}`} onScroll={handleContentScroll}>
              {accFiles.map((item) => {
                return item.type === 'folder' ? (
                  <ContextMenu key={`${item.type}-${item.uuid}`}>
                    <ContextMenuTrigger>
                      <Link
                        to={`/files/${item.uuid}`}
                        data-row-id={item.uuid}
                        title={t('my-docs:hint.doubleClickOpen') || 'Double-click to open'}
                        className="flex flex-col w-[135px] group cursor-pointer"
                        onClick={(e) => {
                          if (isMobileView) return; // tap follows the Link into the folder
                          e.preventDefault();
                          selectRow(e, item);
                        }}
                        onDoubleClick={(e) => { e.preventDefault(); openItem(item); }}
                      >
                        <div className={cn(
                          "w-[135px] h-[135px] rounded-xl flex items-end justify-center pb-3 relative",
                          CARD_GLASS,
                          draggedOverFolderUuid === item.uuid && "!bg-brand/10 ring-2 ring-brand -translate-y-1",
                          selectedUuids.has(item.uuid) && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                          focusUuid === item.uuid && "ring-2 ring-primary"
                        )}
                        onDragOver={(e) => handleFolderDragOver(e, item.uuid)}
                        onDragLeave={handleFolderDragLeave}
                        onDrop={(e) => handleInternalDropToFolder(e, item.uuid)}
                        >
                          {/* Selection checkbox — visible on hover or when selected */}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(item.uuid); }}
                            className={cn(
                              "absolute top-1.5 left-1.5 z-20 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all",
                              selectedUuids.has(item.uuid)
                                ? "bg-primary border-primary text-white opacity-100"
                                : "bg-white/80 border-muted-foreground/40 text-transparent opacity-0 max-md:opacity-100 group-hover:opacity-100 hover:border-primary"
                            )}
                            aria-label={selectedUuids.has(item.uuid) ? t('my-docs:bulk.deselect') : t('my-docs:bulk.select')}
                          >
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </button>
                          <div className="flex justify-center">
                            <div className="relative">
                              <img src="/images/folder.svg" alt={t('my-docs:properties.folder')} className="w-24 h-24 object-contain" />
                              {item.visibility && (
                                <div className="absolute -bottom-1 -right-1">
                                  <VisibilityControl
                                    nodeUuid={item.uuid}
                                    visibility={item.visibility}
                                    isFolder
                                    size={15}
                                    onChanged={refreshData}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Folder name — title only in grid view */}
                        <div className="mt-2 w-full text-center">
                          <TruncatedTooltip content={item.name}>
                            <h3 className="font-medium text-sm text-foreground line-clamp-2 break-words leading-tight cursor-default group-hover:text-blue-600 transition-colors">
                              {item.name}
                            </h3>
                          </TruncatedTooltip>
                        </div>
                      </Link>
                    </ContextMenuTrigger>

                    <ContextMenuContent className="w-56">
                      {nodeMenuItems(item)}
                    </ContextMenuContent>
                  </ContextMenu>
                ) : (() => {
                  const isFileOwner = isOwner(item);
                  // Editor level or above (the owner, or a share at editor) —
                  // not `can_edit`, which is also false while the file is
                  // locked, signed or filed.
                  const hasWriteAccess = atLeast(item, 'editor');
                  const isSharedWithMe = !isFileOwner && hasWriteAccess;
                  return (
                  <ContextMenu key={`${item.type}-${item.uuid}`}>
                    <ContextMenuTrigger className="block">
                      <div
                        data-row-id={item.uuid}
                        title={t('my-docs:hint.doubleClickPreview') || 'Double-click to preview the file'}
                        className="flex flex-col w-[135px] group cursor-pointer"
                        onClick={(e) => { if (isMobileView) { openItem(item); return; } selectRow(e, item); }}
                        onDoubleClick={() => openItem(item)}
                        draggable
                        onDragStart={(e) => handleInternalDragStart(e, item)}
                      >
                        <div className={cn(
                          "w-[135px] h-[135px] rounded-xl p-4 relative flex flex-col justify-between group-active:scale-95 group-active:opacity-50",
                          CARD_GLASS,
                          selectedUuids.has(item.uuid) && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                          focusUuid === item.uuid && "ring-2 ring-primary"
                        )}>
                          {/* Selection checkbox — visible on hover or when selected */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleSelect(item.uuid); }}
                            className={cn(
                              "absolute top-1.5 left-1.5 z-20 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all",
                              selectedUuids.has(item.uuid)
                                ? "bg-primary border-primary text-white opacity-100"
                                : "bg-white/80 border-muted-foreground/40 text-transparent opacity-0 max-md:opacity-100 group-hover:opacity-100 hover:border-primary"
                            )}
                            aria-label={selectedUuids.has(item.uuid) ? t('my-docs:bulk.deselect') : t('my-docs:bulk.select')}
                          >
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </button>

                          {/* Pulsing red dot — file needs the user's action
                              (pending sign or vote). Computed server-side. */}
                          {(item.needsMyAction || item.needs_my_action) && (
                            <span
                              className="absolute top-2 right-2 flex h-2.5 w-2.5"
                              title={t('mainPage:filters.action_required')}
                            >
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                            </span>
                          )}

                          {/* Centered branded file-type icon. Visibility is the
                              small control on the icon's corner — the exact same
                              treatment as folder cards and both list-view rows,
                              so files and folders read identically. w-20 makes the
                              sheet (1:1.2 aspect) 96px tall — the folder icon's
                              height; w-24 would overflow the 135px tile. */}
                          <div className="flex-1 flex items-center justify-center min-h-0">
                            <div className="relative w-20">
                              <FileTypeIcon file={item} />
                              {item.visibility && (
                                <div className="absolute -bottom-1 -right-1">
                                  <VisibilityControl
                                    nodeUuid={item.uuid}
                                    visibility={item.visibility}
                                    size={15}
                                    onChanged={refreshData}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Badges float over the tile's bottom-left corner so a
                              full-height icon never gets squeezed by them. */}
                          <div className="absolute bottom-2 left-2 z-10 flex flex-col gap-1 items-start">
                            {/* E-IMZO Badge */}
                            {item.approvalState === 'signed' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100/80 text-purple-700 border-0 backdrop-blur-sm">
                                <CheckCircle className="w-3 h-3" />
                                <span>E-IMZO</span>
                              </span>
                            )}

                            {/* Agreement Badge */}
                            {item.approvalState === 'agreement' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100/80 text-blue-700 border-0 backdrop-blur-sm">
                                <UserCheck className="w-3 h-3" />
                                <span>{t('my-docs:badges.agreement')}</span>
                              </span>
                            )}

                            {/* "Shared with me" is an access signal, not the file's
                                intrinsic visibility — it keeps its own pill. */}
                            {isSharedWithMe && (
                              <VisibilityBadge
                                visibility={item.visibility}
                                shared
                                className="bg-white/90 backdrop-blur-sm shadow-sm border-0"
                              />
                            )}
                            {/* Quiet when healthy: only exceptional index
                                states surface on cards. */}
                            <IndexStatusBadge
                              status={item.indexStatus || item.index_status}
                              className="backdrop-blur-sm"
                            />
                            {/* Only a trashed template ever reaches a listing. */}
                            <TemplateBadge fileType={item.fileType} className="backdrop-blur-sm" />
                            <RecordBadge node={item} className="backdrop-blur-sm" />
                            <LockBadge node={item} className="backdrop-blur-sm" />
                          </div>
                        </div>

                        {/* File name at bottom */}
                        <div className="mt-3 w-full text-center">
                          <TruncatedTooltip content={fileDisplayName(item)}>
                            <h3 className="font-medium text-sm text-foreground line-clamp-2 break-words leading-tight cursor-default">
                              {fileDisplayName(item)}
                            </h3>
                          </TruncatedTooltip>
                        </div>
                      </div>
                    </ContextMenuTrigger>

                    <ContextMenuContent className="w-56">
                      {nodeMenuItems(item)}
                    </ContextMenuContent>
                  </ContextMenu>
                  );
                })()
              })}
              {/* End-of-list loader (card view) — feedback while more pages load.
                  col-span-full puts it on its own full-width row under the cards
                  instead of occupying a single 135px cell mid-row. */}
              {!sharedView && filesHasMore && (
                <div className="col-span-full flex justify-center py-4">
                  <LoadingSpinner width={24} height={24} strokeWidth={4} color="#999" />
                </div>
              )}
            </div>
          ) : (
            // List View
            <div key={`list-${activeFilter}`} className="flex-1 min-h-0 flex flex-col fade-slide-in">
              {/* Column headers — select-all lives in the floating toolbar. */}
              <div className="hidden sm:flex items-center gap-3 px-2 pb-1.5 mb-1 border-b text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <div className="w-5 shrink-0" />
                <div className="w-10 shrink-0" />
                <button type="button" onClick={() => toggleSort('name')} className="flex-1 min-w-0 flex items-center gap-1 uppercase tracking-wide hover:text-foreground transition-colors cursor-pointer">
                  {t('mainPage:columns.name') || 'Name'} {sortArrow('name')}
                </button>
                <div className="hidden md:block w-28 shrink-0 text-right">
                  {t('mainPage:columns.status') || 'Status'}
                </div>
                <button type="button" onClick={() => toggleSort('size')} className="w-24 flex items-center justify-end gap-1 uppercase tracking-wide hover:text-foreground transition-colors cursor-pointer">
                  {t('mainPage:columns.size') || 'Size'} {sortArrow('size')}
                </button>
                <button type="button" onClick={() => toggleSort('modified')} className="w-32 flex items-center justify-end gap-1 uppercase tracking-wide hover:text-foreground transition-colors cursor-pointer">
                  {t('mainPage:columns.modified') || 'Modified'} {sortArrow('modified')}
                </button>
                <div className="w-8 shrink-0" />
              </div>
              <ul ref={listContainerRef} tabIndex={0} onKeyDown={handleListKeyDown} onMouseDown={handleListBackgroundMouseDown} className={`px-2 pt-2 pb-24 outline-none ${scrollBoxClass}`} onScroll={handleContentScroll}>
              <div style={{ height: `${listVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
              {listVirtualizer.getVirtualItems().map((virtualRow) => {
                const item = accFiles[virtualRow.index];

                const content = item.type === 'folder' ? (
                  <ContextMenu key={`${item.type}-${item.uuid}`}>
                    <ContextMenuTrigger asChild>
                      <Link
                        to={`/files/${item.uuid}`}
                        data-row-id={item.uuid}
                        title={t('my-docs:hint.doubleClickOpen') || 'Double-click to open'}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors cursor-pointer group",
                          selectedUuids.has(item.uuid) && "bg-primary/5 ring-1 ring-primary/40",
                          focusUuid === item.uuid && "ring-2 ring-primary/60"
                        )}
                        onClick={(e) => {
                          if (isMobileView) return; // tap follows the Link into the folder
                          e.preventDefault();
                          selectRow(e, item);
                        }}
                        onDoubleClick={(e) => { e.preventDefault(); openItem(item); }}
                      >
                        {/* Selection checkbox — visible on hover or when selected */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(item.uuid); }}
                          className={cn(
                            "w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all",
                            selectedUuids.has(item.uuid)
                              ? "bg-primary border-primary text-white opacity-100"
                              : "border-muted-foreground/40 text-transparent opacity-0 max-md:opacity-100 group-hover:opacity-100 hover:border-primary"
                          )}
                          aria-label={selectedUuids.has(item.uuid) ? t('my-docs:bulk.deselect') : t('my-docs:bulk.select')}
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </button>
                        {/* Box */}
                        <div
                          className={cn(
                            "w-10 h-10 rounded-[4px] flex items-center justify-center shrink-0 relative transition-all",
                            CARD_SURFACE,
                            draggedOverFolderUuid === item.uuid ? "bg-blue-100 ring-2 ring-blue-500 scale-110" : ""
                          )}
                          onDragOver={(e) => handleFolderDragOver(e, item.uuid)}
                          onDragLeave={handleFolderDragLeave}
                          onDrop={(e) => handleInternalDropToFolder(e, item.uuid)}
                        >
                          <img src="/images/folder.svg" alt={t('my-docs:properties.folder')} className="w-6 h-6 object-contain" />
                          {item.visibility && (
                            <div className="absolute -bottom-1 -right-1">
                              <VisibilityControl
                                nodeUuid={item.uuid}
                                visibility={item.visibility}
                                isFolder
                                size={11}
                                onChanged={refreshData}
                              />
                            </div>
                          )}
                        </div>

                        {/* Name */}
                        <div className="min-w-0 flex-1">
                          <TruncatedTooltip content={item.name}>
                            <span className="text-sm font-medium text-foreground truncate cursor-default">
                              {item.name}
                            </span>
                          </TruncatedTooltip>
                        </div>

                        {/* Status column — N/A for folders, kept for alignment */}
                        <div className="hidden md:block w-28 shrink-0" />
                        {/* Item count + modified columns */}
                        <div className="hidden sm:block w-24 text-right text-xs text-muted-foreground shrink-0">
                          {t('mainPage:items', { count: item.itemCount ?? item.fileCount ?? 0 })}
                        </div>
                        <div className="hidden sm:block w-32 text-right text-xs text-muted-foreground shrink-0">
                          {formatRelativeDate(item.updatedAt || item.createdAt)}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 ml-auto">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:bg-muted relative z-10">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                              {navLocation === 'trash'
                                ? renderTrashMenuItems(item, { Item: DropdownMenuItem, Separator: DropdownMenuSeparator })
                                : renderFolderMenuItems(item, { Item: DropdownMenuItem, Separator: DropdownMenuSeparator })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </Link>
                    </ContextMenuTrigger>

                    <ContextMenuContent className="w-56">
                      {nodeMenuItems(item)}
                    </ContextMenuContent>
                  </ContextMenu>
                ) : (() => {
                  const isFileOwner = isOwner(item);
                  // See card view above — editor level, not `can_edit`.
                  const hasWriteAccess = atLeast(item, 'editor');
                  const isSharedWithMe = !isFileOwner && hasWriteAccess;
                  return (
                  <ContextMenu key={`${item.type}-${item.uuid}`}>
                    <ContextMenuTrigger asChild>
                      <div
                        data-row-id={item.uuid}
                        title={t('my-docs:hint.doubleClickPreview') || 'Double-click to preview the file'}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group",
                          selectedUuids.has(item.uuid) && "bg-primary/5 ring-1 ring-primary/40",
                          focusUuid === item.uuid && "ring-2 ring-primary/60"
                        )}
                        onClick={(e) => { if (isMobileView) { openItem(item); return; } selectRow(e, item); }}
                        onDoubleClick={() => openItem(item)}
                      >
                        {/* Selection checkbox — visible on hover or when selected */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleSelect(item.uuid); }}
                          className={cn(
                            "w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all",
                            selectedUuids.has(item.uuid)
                              ? "bg-primary border-primary text-white opacity-100"
                              : "border-muted-foreground/40 text-transparent opacity-0 max-md:opacity-100 group-hover:opacity-100 hover:border-primary"
                          )}
                          aria-label={selectedUuids.has(item.uuid) ? t('my-docs:bulk.deselect') : t('my-docs:bulk.select')}
                        >
                          <Check className="w-3 h-3" strokeWidth={3} />
                        </button>
                        {/* Box */}
                        <div
                          className={cn(
                            "w-10 h-10 rounded-[4px] flex items-center justify-center shrink-0 relative transition-transform group-active:scale-90 group-active:opacity-50",
                            CARD_SURFACE
                          )}
                          draggable
                          onDragStart={(e) => handleInternalDragStart(e, item)}
                        >
                          <div className="flex items-center justify-center w-7">
                            <FileTypeIcon file={item} />
                          </div>
                          {item.visibility && (
                            <div className="absolute -bottom-1 -right-1">
                              <VisibilityControl
                                nodeUuid={item.uuid}
                                visibility={item.visibility}
                                size={11}
                                onChanged={refreshData}
                              />
                            </div>
                          )}
                        </div>

                        {/* Name + inline badges/status */}
                        <div className="min-w-0 flex-1 flex items-center gap-2">
                          {(item.needsMyAction || item.needs_my_action) && (
                            <span
                              className="relative flex h-2 w-2 flex-shrink-0"
                              title={t('mainPage:filters.action_required')}
                            >
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                            </span>
                          )}
                          <TruncatedTooltip content={fileDisplayName(item)}>
                            <span className="text-sm font-medium text-foreground truncate cursor-default">
                              {fileDisplayName(item)}
                            </span>
                          </TruncatedTooltip>
                          {item.approvalState === 'signed' && (
                            <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100/80 text-purple-700 border-0 shrink-0">
                              <CheckCircle className="w-3 h-3" />
                              <span>E-IMZO</span>
                            </span>
                          )}
                          {item.approvalState === 'agreement' && (
                            <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100/80 text-blue-700 border-0 shrink-0">
                              <UserCheck className="w-3 h-3" />
                              <span>{t('my-docs:badges.agreement')}</span>
                            </span>
                          )}
                          {/* Private/Public is shown by the overlay icon on the file
                              box (matching folders); only the "Shared with me" signal
                              still needs its own pill. */}
                          {isSharedWithMe && <VisibilityBadge visibility={item.visibility} shared />}
                          <TemplateBadge fileType={item.fileType} className="hidden md:inline-flex shrink-0" />
                          <RecordBadge node={item} className="shrink-0" />
                          <LockBadge node={item} className="shrink-0" />
                        </div>

                        {/* Status column (index state; quiet green check
                            when healthy) */}
                        <div className="hidden md:flex w-28 shrink-0 justify-end">
                          <IndexStatusBadge
                            status={item.indexStatus || item.index_status}
                            showIndexed
                          />
                        </div>
                        {/* Size + modified columns */}
                        <div className="hidden sm:block w-24 text-right text-xs text-muted-foreground shrink-0">
                          {formatFileSize(item.size)}
                        </div>
                        <div className="hidden sm:block w-32 text-right text-xs text-muted-foreground shrink-0">
                          {formatRelativeDate(item.updatedAt || item.createdAt)}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center ml-auto">

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:bg-muted relative z-10">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                              {navLocation === 'trash'
                                ? renderTrashMenuItems(item, { Item: DropdownMenuItem, Separator: DropdownMenuSeparator })
                                : renderFileMenuItems(item, { Item: DropdownMenuItem, Separator: DropdownMenuSeparator })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </ContextMenuTrigger>

                    <ContextMenuContent className="w-56">
                      {nodeMenuItems(item)}
                    </ContextMenuContent>
                  </ContextMenu>
                  );
                })();
                return (
                  <li
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={listVirtualizer.measureElement}
                    className="absolute top-0 left-0 w-full pb-1"
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                  >
                    {content}
                  </li>
                );
              })}
              </div>
              {/* End-of-list loader: shown while more pages are available so the
                  user gets feedback during infinite-scroll fetches. */}
              {!sharedView && filesHasMore && (
                <li className="flex justify-center py-4">
                  <LoadingSpinner width={24} height={24} strokeWidth={4} color="#999" />
                </li>
              )}
              </ul>
            </div>
          )
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full text-center p-8 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-blue-50 dark:bg-blue-900/10 rounded-full flex items-center justify-center mb-6 ring-8 ring-blue-50/50 dark:ring-blue-900/5">
              {isSearching
                ? <SearchIcon className="w-12 h-12 text-blue-500/80" strokeWidth={1.5} />
                : <FolderLucide className="w-12 h-12 text-blue-500/80" strokeWidth={1.5} />}
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3 tracking-tight">
              {isSearching
                ? (t('mainPage:empty.noResultsTitle') || 'No matches')
                : activeFilter === 'trash'
                  ? (t('mainPage:empty.trashTitle') || 'Trash is empty')
                  : t(activeFilter === 'archived' ? 'mainPage:empty.archivedTitle' : 'mainPage:empty.title')}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-[400px] leading-relaxed text-[15px]">
              {isSearching
                ? (t('mainPage:empty.noResultsText', { query: debouncedSearch }) || `Nothing matches “${debouncedSearch}” here.`)
                : activeFilter === 'trash'
                  ? (t('mainPage:empty.trashDescription') || 'Deleted items show up here.')
                  : t(activeFilter === 'archived' ? 'mainPage:empty.archivedDescription' : 'mainPage:empty.description')}
            </p>
            {/* No create CTA while searching or in Archive/Trash. */}
            {!isSearching && activeFilter !== 'archived' && activeFilter !== 'trash' && (
              <Button
                onClick={() => setOpen(true)}
                size="lg"
                className="rounded-full px-8 shadow-lg shadow-blue-500/20 bg-brand hover:bg-[#324E8C] transition-all hover:scale-105 active:scale-95"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('mainPage:empty.createButton')}
              </Button>
            )}
            {/* A user whose listing filter found nothing is the prime
                global-search user — hand them the palette. */}
            {isSearching && (
              <Button
                variant="ghost"
                onClick={() => openCommandPalette()}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <SearchIcon className="w-4 h-4" />
                {t('search:everywhere')}
                <KbdChip>{shortcutLabel()}</KbdChip>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Right-click-empty-canvas create menu (same items as "+ New"). */}
      {createMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setCreateMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setCreateMenu(null); }}
          />
          <div
            className="fixed z-50 min-w-[200px] max-w-[calc(100vw-16px)] rounded-[4px] border border-border bg-popover p-1.5 shadow-lg"
            style={{ left: createMenu.x, top: createMenu.y }}
          >
            {renderCreateMenuItems(canvasMenuPrimitives)}
          </div>
        </>
      )}

      {registering && (
        <RegisterDialog open file={registering} onOpenChange={(open) => { if (!open) setRegistering(null); }} onDone={() => refreshData()} />
      )}
      {filing && (
        <FileToCaseDialog
          open
          node={filing.node}
          mode={filing.mode}
          onOpenChange={(open) => { if (!open) setFiling(null); }}
          onDone={() => refreshData()}
        />
      )}

      {/* Dialogs */}
      <DeleteFolder
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        folder={selectedFolder}
        setData={refreshData}
      />
      <UpdateFolder
        open={updateOpen}
        onOpenChange={setUpdateOpen}
        folder={selectedFolder}
        setData={refreshData}
      />
      <RenameFile
        open={renameOpen}
        onOpenChange={setRenameOpen}
        file={selectedFile}
        setData={refreshData}
      />

      <TransferOwnerModal
        open={Boolean(handingOver)}
        setOpen={(value) => { if (!value) setHandingOver(null); }}
        node={handingOver}
        onDone={() => {
          refreshData();
          if (sharedMode === 'received') getReceivedShared.request();
        }}
      />

      {/* Share Folder Modal */}
      {selectedFolder && (
        <SharingModal
          openShare={shareFolderOpen}
          setOpenShare={setShareFolderOpen}
          nodeUuid={selectedFolder.uuid}
          item={selectedFolder}
          type="folder"
        />
      )}
      <DeleteFile
        open={deleteFileOpen}
        onOpenChange={setDeleteFileOpen}
        file={selectedFile}
        setData={refreshData}
      />
      <SharingModal
        openShare={shareFileOpen}
        setOpenShare={setShareFileOpen}
        nodeUuid={selectedFile?.uuid}
        file={selectedFile}
        type="file"
      />
      <ChangeVisibilityModal
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
        file={selectedFile}
        setData={refreshData}
      />
      <PropertiesModal
        open={propsOpen}
        setOpen={setPropsOpen}
        resource={propsResource}
        type={propsType}
      />
      <DeleteShareModal
        open={sharedDeleteOpen}
        setOpen={setSharedDeleteOpen}
        file={selectedShareItem}
        setData={() => {
          if (sharedMode === 'received') getReceivedShared.request();
          else getSentShared.request();
        }}
      />
      <VersionHistoryModal
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        file={selectedFile}
        setData={refreshData}
      />
      <DocumentTypeSelector
        open={docTypeOpen}
        onOpenChange={setDocTypeOpen}
        fileUuid={selectedFile?.uuid}
        fileName={selectedFile?.name}
        currentType={selectedFile?.approvalState || 'ordinary'}
        onSuccess={() => refreshData()}
      />
      <ActivityModal
        open={activityOpen}
        onOpenChange={setActivityOpen}
        file={selectedFile}
      />
      {grantsOn && (
        <AllowAssistantDialog nodes={assistantNodes} onOpenChange={(value) => { if (!value) setAssistantNodes(null); }} />
      )}
      {/* Direction-aware "scroll to top" — only while scrolling up, well down
          the list. Bottom-right so it clears the bottom-center bulk toolbar. */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          aria-label={t('my-docs:actions.scrollTop') || 'Scroll to top'}
          title={t('my-docs:actions.scrollTop') || 'Scroll to top'}
          className="fixed bottom-[calc(2.5rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-50 flex h-11 w-11 items-center justify-center rounded-full border bg-background/95 shadow-lg backdrop-blur transition-colors hover:bg-muted animate-in fade-in slide-in-from-bottom-2"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
      {/* Shown only from two rows up: acting on a single row is what the
          context menu is for. */}
      {selectionCount > 1 && (
        <BulkActionBar
          count={selectionCount}
          allSelected={accFiles.length > 0 && accFiles.every((i) => selectedUuids.has(i.uuid))}
          onToggleSelectAll={() => toggleSelectAll(accFiles)}
          inTrash={navLocation === 'trash'}
          onRestore={mayDelete ? handleBulkRestore : undefined}
          restoreLoading={restoreTrash.loading}
          onPurge={mayDelete ? () => askPurge([...selectedUuids]) : undefined}
          purgeLoading={purgeNode.loading}
          onMove={mayMove ? () => openBulkMove(false) : undefined}
          onCopy={mayCopy ? () => openBulkMove(true) : undefined}
          copyDisabled={hasFolderSelection}
          onDelete={mayDelete ? () => setBulkDeleteOpen(true) : undefined}
          deleteLoading={bulkDelete.loading || archiveFile.loading}
          onAllowAssistant={grantsOn && navLocation !== 'trash' && mayAllow(selectedNodes)
            ? () => setAssistantNodes(selectedNodes)
            : undefined}
          onClear={clearSelection}
        />
      )}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        deletableCount={bulkDeletableCount}
        signedCount={selectedSignedCount}
        onConfirm={handleConfirmBulkDelete}
        loading={bulkDelete.loading || archiveFile.loading}
      />
      <PermanentDeleteDialog
        open={purgeOpen}
        onOpenChange={setPurgeOpen}
        count={purgeUuids.length}
        onConfirm={confirmPurge}
        loading={purgeNode.loading}
      />
      <MoveFileModal
        open={moveFileOpen}
        onOpenChange={(v) => { setMoveFileOpen(v); if (!v) { setBulkFiles([]); setBulkFolderUuids([]); } }}
        file={selectedFile}
        files={bulkFiles}
        folderUuids={bulkFolderUuids}
        mode={moveMode}
        onMoveSuccess={() => { refreshData(); clearSelection(); setBulkFiles([]); setBulkFolderUuids([]); }}
      />
      <CommentsDrawer
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        file={selectedFile}
      />
      <DocumentEditorModal
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
        }}
        doc={selectedFile}
        onSave={refreshData}
        collaborative={true}
      />
      <DocumentModal
        open={!!previewDoc}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onEdit={
          // Same gate as the context menu's Edit item (`edit_block` /
          // `can_edit` from the backend).
          previewDoc && canEditFile(previewDoc)
            ? () => {
                const docToEdit = previewDoc;
                setPreviewDoc(null);
                handleRequestEdit(docToEdit);
              }
            : undefined
        }
        onShare={previewDoc && can(previewDoc, 'share') ? () => {
          setSelectedFile(previewDoc);
          setShareFileOpen(true);
        } : undefined}
      />

      {/* "From template…" picker — management lives on /templates. Mounted
          only while open so the library is fetched on demand, not per render. */}
      {templatesModalOpen && (
        <TemplatePicker
          open
          onOpenChange={setTemplatesModalOpen}
          onSelect={handleCreateFromTemplate}
        />
      )}
      <FillFromRecordDialog
        open={Boolean(fillingTemplate)}
        template={fillingTemplate?.template}
        language={fillingTemplate?.language}
        busy={cloneFile.loading}
        onCancel={() => setFillingTemplate(null)}
        onCreate={handleFilledFromRecord}
      />

      <LockFileDialog
        file={locking?.file || null}
        mode={locking?.mode}
        onOpenChange={(open) => { if (!open) setLocking(null); }}
        onDone={refreshData}
      />

      <ArchiveConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        onConfirm={handleConfirmArchive}
        loading={archiveFile.loading}
      />
      <RestoreConfirmDialog
        open={restoreOpen}
        onOpenChange={setRestoreOpen}
        onConfirm={handleConfirmRestore}
        loading={restoreFile.loading}
        needsFolderPick={restoreNeedsPick}
      />
    </div>
  );
};
